import { useState, useEffect, useRef, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios, { getApiUrl } from "@/lib/api";
import { 
  Plus, Search, Gamepad2, Trophy, Filter, ImageIcon, Upload, 
  Link as LinkIcon, ArrowUpDown, Globe, Loader2, ExternalLink,
  ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Package, Star, Database, Download,
  AlertTriangle, Info, Archive, Tag, Clock, BookOpen, Edit2, Check, Save, Trash2, CheckCircle2, EyeOff, Copy, ShieldCheck, Unlink, User
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "sonner";
import PlatformBadge, { SystemBadge } from "@/components/PlatformBadge";
import SlopBadge from "@/components/SlopBadge";
import { Progress } from "@/components/ui/progress";
import SystemBarOverlay from "@/components/SystemBarOverlay";
import { AdBanner } from "@/components/AdSense";
import { GamesSkeleton } from "@/components/Skeleton";
import PlatformSystemPicker from "@/components/PlatformSystemPicker";

const API = `${getApiUrl()}/api`;

const SUPPORTED_SITES = [
  { name: "MetaGamerScore", url: "metagamerscore.com", platform: "Multi", note: "Game info only (add achievements manually)" },
];
import { buildSystemLookup, getSystemName } from "@/utils/systemNames";


export default function Games() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [games, setGames] = useState([]);
  const [dbStatusMap, setDbStatusMap] = useState({});
  const [platforms, setPlatforms] = useState([]);
  const [systems, setSystems] = useState({});
  const [loading, setLoading] = useState(true);
  const isInitialMount = useRef(true);
  
  // Build system name lookup from systems data
  const systemsLookup = useMemo(() => buildSystemLookup(systems), [systems]);
  
  // Initialize state from URL params
  const [searchQuery, setSearchQuery] = useState(searchParams.get("search") || "");
  const [debouncedSearch, setDebouncedSearch] = useState(searchParams.get("search") || "");
  const [platformFilter, setPlatformFilter] = useState(searchParams.get("platform") || "all");
  const [ownedFilter, setOwnedFilter] = useState(searchParams.get("owned") || "all");
  const [syncFilter, setSyncFilter] = useState(searchParams.get("sync") || "all");
  const [sortBy, setSortBy] = useState(searchParams.get("sortBy") || "name");
  const [sortOrder, setSortOrder] = useState(searchParams.get("sortOrder") || "asc");
  
  // Debounce search query - only update after 300ms of no typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);
  
  // Genre filter
  const [genres, setGenres] = useState([]);
  const [genreFilters, setGenreFilters] = useState(() => {
    const param = searchParams.get("genres");
    return param ? param.split(",") : [];
  });
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get("page")) || 1);
  const [gamesPerPage, setGamesPerPage] = useState(50);
  const [totalGames, setTotalGames] = useState(0);

  const goToPage = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  // User preferences (loaded from backend)
  const [preferencesLoaded, setPreferencesLoaded] = useState(false);
  
  // Game tag filter (Personal Tags)
  const [gameTags, setGameTags] = useState([]);
  const [gameTagFilter, setGameTagFilter] = useState(searchParams.get("userTag") || "all");
  
  // System filter (shown when platform has multiple systems)
  const [systemFilter, setSystemFilter] = useState(searchParams.get("system") || "all");
  const [regionFilter, setRegionFilter] = useState(searchParams.get("region") || "all");
  const [regions, setRegions] = useState([]);
  
  // Content tag filter (MDB achievement-level tags like Missable, Grind, etc.)
  const [masterTags, setMasterTags] = useState([]);
  const [contentTagFilter, setContentTagFilter] = useState(searchParams.get("contentTag") || "all");
  
  // Reveal hidden games (temporary, does not affect settings)
  const [revealHidden, setRevealHidden] = useState(searchParams.get("showHidden") === "true");
  
  // User preferences state for hidden games setting
  const [showHiddenSetting, setShowHiddenSetting] = useState(false);
  
  // Genres dropdown open state (collapsible in filter panel)
  const [genresDropdownOpen, setGenresDropdownOpen] = useState(false);
  
  // Sorted platforms (alphabetical, for filter dropdown)
  const sortedPlatforms = useMemo(() => 
    [...platforms].sort((a, b) => a.name.localeCompare(b.name)),
    [platforms]
  );
  
  // Available systems for selected platform (alphabetical, only when >1)
  const selectedPlatformSystems = useMemo(() => {
    if (platformFilter === "all") return [];
    const platformSystems = systems[platformFilter] || [];
    return [...platformSystems].sort((a, b) => a.name.localeCompare(b.name));
  }, [platformFilter, systems]);
  
  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [incompleteSyncDialogOpen, setIncompleteSyncDialogOpen] = useState(false);
  const [selectedIncompleteGame, setSelectedIncompleteGame] = useState(null);
  const [manualEntryDialogOpen, setManualEntryDialogOpen] = useState(false);
  const [manualEntryGame, setManualEntryGame] = useState(null);
  const [imageMode, setImageMode] = useState("url");
  
  // Bulk edit states
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedGameIds, setSelectedGameIds] = useState([]);
  const [editQueue, setEditQueue] = useState([]);
  const [currentEditIndex, setCurrentEditIndex] = useState(0);
  const [bulkEditDialogOpen, setBulkEditDialogOpen] = useState(false);
  const [bulkEditGameData, setBulkEditGameData] = useState(null);
  const [bulkEditSaving, setBulkEditSaving] = useState(false);
  
  // Auto-sync and bulk add states
  const [autoSyncLoading, setAutoSyncLoading] = useState(false);
  const [bulkAchievementText, setBulkAchievementText] = useState("");
  const [bulkAddLoading, setBulkAddLoading] = useState(false);
  
  // Import states
  const [importUrl, setImportUrl] = useState("");
  const [importLoading, setImportLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  
  // IGDB search states
  const [igdbSearch, setIgdbSearch] = useState("");
  const [igdbResults, setIgdbResults] = useState([]);
  const [igdbLoading, setIgdbLoading] = useState(false);
  const [selectedIgdbGame, setSelectedIgdbGame] = useState(null);
  const [igdbImportPlatform, setIgdbImportPlatform] = useState("");
  const [igdbImportSystem, setIgdbImportSystem] = useState("");
  
  // Xbox/OpenXBL search states
  const [xboxSearch, setXboxSearch] = useState("");
  const [xboxResults, setXboxResults] = useState([]);
  const [xboxLoading, setXboxLoading] = useState(false);
  const [selectedXboxGame, setSelectedXboxGame] = useState(null);
  const [xboxImportSystem, setXboxImportSystem] = useState("");
  const [xboxGamertagConfigured, setXboxGamertagConfigured] = useState(false);
  
  // Steam search states
  const [steamSearch, setSteamSearch] = useState("");
  const [steamResults, setSteamResults] = useState([]);
  const [steamLoading, setSteamLoading] = useState(false);
  const [selectedSteamGame, setSelectedSteamGame] = useState(null);
  const [steamConfigured, setSteamConfigured] = useState(false);
  
  // PlayStation search states
  const [psnSearch, setPsnSearch] = useState("");
  const [psnResults, setPsnResults] = useState([]);
  const [psnLoading, setPsnLoading] = useState(false);
  const [selectedPsnGame, setSelectedPsnGame] = useState(null);
  const [psnConfigured, setPsnConfigured] = useState(false);
  
  // Epic Games manual add states
  const [epicGameName, setEpicGameName] = useState("");
  const [epicBoxArtUrl, setEpicBoxArtUrl] = useState("");
  const [epicAddLoading, setEpicAddLoading] = useState(false);
  
  // Master DB search states
  const [masterDbSearch, setMasterDbSearch] = useState("");
  const [masterDbResults, setMasterDbResults] = useState([]);
  const [masterDbLoading, setMasterDbLoading] = useState(false);
  const [masterDbImporting, setMasterDbImporting] = useState(false);
  const [addGameMode, setAddGameMode] = useState("masterdb"); // "masterdb", "manual", or "titleid"
  
  // Import by Title ID states
  const [titleIdInput, setTitleIdInput] = useState("");
  const [titleIdGameName, setTitleIdGameName] = useState("");
  const [titleIdImporting, setTitleIdImporting] = useState(false);
  
  // Post-import sync prompt states
  const [syncPromptOpen, setSyncPromptOpen] = useState(false);
  const [importedGameForSync, setImportedGameForSync] = useState(null);
  const [postImportSyncing, setPostImportSyncing] = useState(false);
  
  const [newGame, setNewGame] = useState({
    name: "",
    platform: "",
    system: "",
    boxArtUrl: "",
  });

  // Calculate pagination
  const totalPages = Math.ceil(totalGames / gamesPerPage);
  const startIndex = (currentPage - 1) * gamesPerPage;
  const endIndex = startIndex + gamesPerPage;
  const displayedGames = games.slice(startIndex, endIndex);

  useEffect(() => {
    fetchPlatformsAndSystems();
    fetchUserPreferences();
    fetchGenres();
    fetchMasterTags();
  }, []);

  const fetchGenres = async () => {
    try {
      const response = await axios.get(`${API}/master/genres`);
      setGenres(response.data);
    } catch (error) {
      console.error("Failed to fetch genres:", error);
    }
  };

  const fetchMasterTags = async () => {
    try {
      const response = await axios.get(`${API}/master-tags`);
      setMasterTags(response.data);
    } catch (error) {
      console.error("Failed to fetch master tags:", error);
    }
  };

  // Helper to get genre display name from slug
  const getGenreDisplayName = (slug) => {
    const genre = genres.find(g => g.slug === slug);
    return genre ? genre.name : slug;
  };

  const fetchUserPreferences = async () => {
    try {
      const response = await axios.get(`${API}/settings/preferences`);
      if (response.data.gamesPerPage) {
        setGamesPerPage(response.data.gamesPerPage);
      }
      setShowHiddenSetting(!!response.data.showHiddenGames);
      setPreferencesLoaded(true);
    } catch (error) {
      console.error("Failed to fetch preferences:", error);
      setPreferencesLoaded(true);
    }
  };

  // Sync URL params when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (searchQuery) params.set("search", searchQuery);
    if (platformFilter && platformFilter !== "all") params.set("platform", platformFilter);
    if (systemFilter && systemFilter !== "all") params.set("system", systemFilter);
    if (regionFilter && regionFilter !== "all") params.set("region", regionFilter);
    if (ownedFilter && ownedFilter !== "all") params.set("owned", ownedFilter);
    if (syncFilter && syncFilter !== "all") params.set("sync", syncFilter);
    if (contentTagFilter && contentTagFilter !== "all") params.set("contentTag", contentTagFilter);
    if (genreFilters.length > 0) params.set("genres", genreFilters.join(","));
    if (sortBy && sortBy !== "name") params.set("sortBy", sortBy);
    if (sortOrder && sortOrder !== "asc") params.set("sortOrder", sortOrder);
    if (currentPage > 1) params.set("page", currentPage.toString());
    if (gameTagFilter && gameTagFilter !== "all") params.set("userTag", gameTagFilter);
    if (revealHidden) params.set("showHidden", "true");
    
    setSearchParams(params, { replace: true });
  }, [searchQuery, platformFilter, systemFilter, ownedFilter, syncFilter, contentTagFilter, genreFilters, sortBy, sortOrder, currentPage, gameTagFilter, revealHidden, setSearchParams, regionFilter]);

  // Restore scroll position after games load
  useEffect(() => {
    if (!loading && games.length > 0) {
      // Check sessionStorage for saved scroll position
      const scrollPos = sessionStorage.getItem('games_scroll_position');
      
      if (scrollPos) {
        // Clear session storage after using
        sessionStorage.removeItem('games_scroll_position');
        // Use setTimeout to ensure DOM has updated with content
        setTimeout(() => {
          window.scrollTo(0, parseInt(scrollPos) || 0);
        }, 50);
      }
    }
  }, [loading, games.length]);

  // Save scroll position before navigating away (using mousedown to capture before navigation)
  useEffect(() => {
    const handleMouseDown = (e) => {
      const link = e.target.closest('a');
      if (link && link.href && link.href.includes('/games/') && !window.location.pathname.startsWith('/games/')) {
        // Only save when navigating TO a game detail page FROM the games list
        if (link.href.match(/\/games\/[a-zA-Z0-9-]+$/)) {
          sessionStorage.setItem('games_scroll_position', window.scrollY.toString());
        }
      }
    };
    
    document.addEventListener('mousedown', handleMouseDown, true);
    return () => document.removeEventListener('mousedown', handleMouseDown, true);
  }, []);

  useEffect(() => {
    if (platforms.length > 0) {
      fetchGames();
      // Only reset to page 1 on filter changes after initial mount
      if (isInitialMount.current) {
        isInitialMount.current = false;
      } else {
        setCurrentPage(1);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [platforms.length, platformFilter, systemFilter, debouncedSearch, ownedFilter, syncFilter, gameTagFilter, contentTagFilter, genreFilters, revealHidden, sortBy, sortOrder, regionFilter]);

  const fetchPlatformsAndSystems = async () => {
    try {
      const [platformsRes, systemsRes] = await Promise.all([
        axios.get(`${API}/platforms`),
        axios.get(`${API}/systems`),
      ]);
      setPlatforms(platformsRes.data);
      setSystems(systemsRes.data);
      
      // Fetch game tags
      const gameTagsRes = await axios.get(`${API}/game-tags`);
      setGameTags(gameTagsRes.data);
      // Fetch admin-managed regions for the region filter dropdown
      axios.get(`${API}/regions`).then((r) => setRegions(r.data || [])).catch(() => {});
    } catch (error) {
      console.error("Failed to fetch platforms:", error);
    }
  };

  const fetchGames = async () => {
    try {
      const params = new URLSearchParams();
      if (platformFilter && platformFilter !== "all") {
        params.append("platform", platformFilter);
      }
      if (debouncedSearch) {
        params.append("search", debouncedSearch);
      }
      if (ownedFilter && ownedFilter !== "all") {
        params.append("owned", ownedFilter);
      }
      if (syncFilter && syncFilter !== "all") {
        params.append("sync", syncFilter);
      }
      if (gameTagFilter && gameTagFilter !== "all") {
        params.append("userTag", gameTagFilter);
      }
      if (genreFilters.length > 0) {
        params.append("genres", genreFilters.join(","));
      }
      if (systemFilter && systemFilter !== "all") {
        params.append("system", systemFilter);
      }
      if (regionFilter && regionFilter !== "all") {
        params.append("region", regionFilter);
      }
      if (contentTagFilter && contentTagFilter !== "all") {
        params.append("contentTag", contentTagFilter);
      }
      if (revealHidden) {
        params.append("showHidden", "true");
      }
      params.append("sortBy", sortBy);
      params.append("sortOrder", sortOrder);
      const response = await axios.get(`${API}/games?${params.toString()}`);
      setGames(response.data);
      setTotalGames(response.data.length);
      axios.get(`${API}/games-db-status`).then((r) => setDbStatusMap(r.data || {})).catch(() => {});
    } catch (error) {
      console.error("Failed to fetch games:", error);
      toast.error("Failed to load games");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateGame = async () => {
    if (!newGame.name || !newGame.platform || !newGame.system) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      await axios.post(`${API}/games`, newGame);
      toast.success("Game added successfully!");
      setDialogOpen(false);
      setNewGame({ name: "", platform: "", system: "", boxArtUrl: "" });
      fetchGames();
    } catch (error) {
      console.error("Failed to create game:", error);
      toast.error("Failed to add game");
    }
  };

  // Master DB search functions
  const handleMasterDbSearch = async () => {
    if (!masterDbSearch.trim()) return;
    
    setMasterDbLoading(true);
    try {
      const response = await axios.get(`${API}/master/games?search=${encodeURIComponent(masterDbSearch)}&limit=20`);
      const games = response.data?.games || [];
      setMasterDbResults(games);
      if (games.length === 0) {
        toast.info("No games found in Master Database. You can add manually.");
      }
    } catch (error) {
      console.error("Failed to search Master DB:", error);
      toast.error("Failed to search Master Database");
      setMasterDbResults([]);
    } finally {
      setMasterDbLoading(false);
    }
  };
  
  const handleImportFromMasterDb = async (game) => {
    const gameToImport = game;
    if (!gameToImport) {
      toast.error("Please select a game");
      return;
    }
    
    // Check if game has platforms defined
    if (!gameToImport.platforms || gameToImport.platforms.length === 0) {
      toast.error("This game has no platforms defined. Please add platforms in the Master Database first.");
      return;
    }
    
    setMasterDbImporting(true);
    let importedGame = null;
    
    try {
      const response = await axios.post(`${API}/master/import-to-library/${gameToImport.id}`);
      toast.success(`Imported "${gameToImport.name}" with ${response.data.achievementsImported} achievements, ${response.data.dlcsImported} DLCs`);
      importedGame = response.data.game;
      setDialogOpen(false);
      resetMasterDbState();
      fetchGames();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to import game");
      setMasterDbImporting(false);
      return;
    }
    
    setMasterDbImporting(false);
    
    // Check if platform account is linked and prompt for sync (separate try/catch)
    if (importedGame) {
      try {
        const canSync = await checkPlatformSyncAvailable(importedGame.platform);
        if (canSync) {
          setImportedGameForSync(importedGame);
          setSyncPromptOpen(true);
        }
      } catch (e) {
        // Sync check failed, but import succeeded - don't show error
        console.log("Sync check failed:", e);
      }
    }
  };
  
  // Import game by Xbox Title ID
  const handleImportByTitleId = async () => {
    if (!titleIdInput.trim()) {
      toast.error("Please enter a Title ID");
      return;
    }
    
    setTitleIdImporting(true);
    try {
      const response = await axios.post(`${API}/xbox/oauth/import-by-title-id`, {
        titleId: titleIdInput.trim(),
        gameName: titleIdGameName.trim() || null,
      });
      
      if (response.data.alreadyExists) {
        toast.info(response.data.message);
      } else {
        toast.success(response.data.message);
      }
      
      setDialogOpen(false);
      setTitleIdInput("");
      setTitleIdGameName("");
      fetchGames();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to import game by Title ID");
    } finally {
      setTitleIdImporting(false);
    }
  };
  
  // Check if platform sync is available (account linked)
  const checkPlatformSyncAvailable = async (platform) => {
    try {
      if (platform === "xbox") {
        const response = await axios.get(`${API}/xbox/settings`);
        return !!response.data?.gamertag;
      } else if (platform === "steam") {
        const response = await axios.get(`${API}/steam/settings`);
        return !!response.data?.steamId;
      } else if (platform === "playstation") {
        const response = await axios.get(`${API}/playstation/settings`);
        return !!response.data?.onlineId;
      }
      return false;
    } catch {
      return false;
    }
  };
  
  // Handle post-import sync
  const handlePostImportSync = async () => {
    if (!importedGameForSync) return;
    
    setPostImportSyncing(true);
    const platform = importedGameForSync.platform;
    const gameId = importedGameForSync.id;
    
    try {
      let response;
      if (platform === "xbox") {
        response = await axios.post(`${API}/xbox/game/${gameId}/sync-achievements`);
      } else if (platform === "steam") {
        response = await axios.post(`${API}/steam/game/${gameId}/sync-achievements`);
      } else if (platform === "playstation") {
        response = await axios.post(`${API}/playstation/game/${gameId}/sync-trophies`);
      }
      
      if (response?.data) {
        const { added, updated, unlocked } = response.data;
        if (unlocked > 0) {
          toast.success(`Synced! ${unlocked} achievements marked as unlocked`);
        } else if (added > 0 || updated > 0) {
          toast.success(`Synced ${added || 0} achievements, updated ${updated || 0}`);
        } else {
          toast.info("Sync complete - no unlocked achievements found yet");
        }
      }
      
      setSyncPromptOpen(false);
      setImportedGameForSync(null);
      fetchGames();
    } catch (error) {
      console.error("Post-import sync failed:", error);
      toast.error(error.response?.data?.detail || "Failed to sync achievements");
    } finally {
      setPostImportSyncing(false);
    }
  };
  
  const skipPostImportSync = () => {
    setSyncPromptOpen(false);
    setImportedGameForSync(null);
  };
  
  // Bulk Edit Functions
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  
  const enterSelectionMode = () => {
    setSelectionMode(true);
    setSelectedGameIds([]);
  };
  
  const exitSelectionMode = () => {
    setSelectionMode(false);
    setSelectedGameIds([]);
    setEditQueue([]);
    setCurrentEditIndex(0);
  };
  
  const toggleGameSelection = (gameId) => {
    setSelectedGameIds(prev => 
      prev.includes(gameId) 
        ? prev.filter(id => id !== gameId)
        : [...prev, gameId]
    );
  };
  
  const selectAllOnPage = () => {
    const pageGameIds = displayedGames.map(g => g.id);
    const allSelected = pageGameIds.every(id => selectedGameIds.includes(id));
    
    if (allSelected) {
      // Deselect all on page
      setSelectedGameIds(prev => prev.filter(id => !pageGameIds.includes(id)));
    } else {
      // Select all on page
      setSelectedGameIds(prev => [...new Set([...prev, ...pageGameIds])]);
    }
  };
  
  const startBulkEdit = () => {
    if (selectedGameIds.length === 0) return;
    
    // Build queue of games to edit
    const queue = selectedGameIds
      .map(id => games.find(g => g.id === id))
      .filter(Boolean);
    
    setEditQueue(queue);
    setCurrentEditIndex(0);
    
    // Load first game data
    if (queue.length > 0) {
      setBulkEditGameData({
        ...queue[0],
        genres: queue[0].genres || [],
        gameTags: queue[0].gameTags || [],
      });
      setBulkEditDialogOpen(true);
    }
  };
  
  const handleBulkEditSave = async () => {
    if (!bulkEditGameData) return;
    
    setBulkEditSaving(true);
    try {
      await axios.put(`${API}/games/${bulkEditGameData.id}`, {
        name: bulkEditGameData.name,
        platform: bulkEditGameData.platform,
        system: bulkEditGameData.system,
        publisher: bulkEditGameData.publisher,
        developer: bulkEditGameData.developer,
        releaseDate: bulkEditGameData.releaseDate,
        genres: bulkEditGameData.genres,
        gameTags: bulkEditGameData.gameTags,
        isPriority: bulkEditGameData.isPriority,
        isBacklog: bulkEditGameData.isBacklog,
        isManualComplete: bulkEditGameData.isManualComplete,
        isHidden: bulkEditGameData.isHidden,
      });
      toast.success(`Saved "${bulkEditGameData.name}"`);
    } catch (error) {
      console.error("Failed to save game:", error);
      toast.error("Failed to save changes");
    } finally {
      setBulkEditSaving(false);
      moveToNextBulkEdit();
    }
  };
  
  const handleBulkEditSkip = () => {
    moveToNextBulkEdit();
  };
  
  const moveToNextBulkEdit = () => {
    const nextIndex = currentEditIndex + 1;
    
    if (nextIndex < editQueue.length) {
      setCurrentEditIndex(nextIndex);
      const nextGame = editQueue[nextIndex];
      setBulkEditGameData({
        ...nextGame,
        genres: nextGame.genres || [],
        gameTags: nextGame.gameTags || [],
      });
    } else {
      // All done - close dialog and exit selection mode
      setBulkEditDialogOpen(false);
      setBulkEditGameData(null);
      exitSelectionMode();
      fetchGames(); // Refresh the list
      toast.success("Bulk edit complete!");
    }
  };
  
  const handleBulkDelete = async () => {
    if (selectedGameIds.length === 0) return;
    
    setBulkDeleting(true);
    let deleted = 0;
    let failed = 0;
    
    for (const gameId of selectedGameIds) {
      try {
        await axios.delete(`${API}/games/${gameId}`);
        deleted++;
      } catch (error) {
        console.error(`Failed to delete game ${gameId}:`, error);
        failed++;
      }
    }
    
    setBulkDeleting(false);
    setBulkDeleteDialogOpen(false);
    exitSelectionMode();
    fetchGames();
    
    if (failed > 0) {
      toast.warning(`Deleted ${deleted} game(s), ${failed} failed`);
    } else {
      toast.success(`Deleted ${deleted} game(s)`);
    }
  };
  
  const resetMasterDbState = () => {
    setMasterDbSearch("");
    setMasterDbResults([]);
    setAddGameMode("masterdb");
  };

  const handleImageUpload = async (file) => {
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image must be less than 10MB");
      return;
    }

    try {
      // Get signed upload params from backend
      const signatureRes = await axios.get(`${API}/cloudinary/signature?folder=gamepop/games`);
      const sig = signatureRes.data;

      // Upload directly to Cloudinary
      const formData = new FormData();
      formData.append("file", file);
      formData.append("api_key", sig.api_key);
      formData.append("timestamp", sig.timestamp);
      formData.append("signature", sig.signature);
      formData.append("folder", sig.folder);
      formData.append("transformation", sig.transformation);

      const uploadRes = await fetch(
        `https://api.cloudinary.com/v1_1/${sig.cloud_name}/image/upload`,
        { method: "POST", body: formData }
      );

      if (!uploadRes.ok) {
        throw new Error("Upload failed");
      }

      const uploadData = await uploadRes.json();
      setNewGame({ ...newGame, boxArtUrl: uploadData.secure_url });
      toast.success("Image uploaded!");
    } catch (error) {
      console.error("Failed to upload image:", error);
      toast.error("Failed to upload image");
    }
  };

  // Import from URL functions
  const handlePreviewUrl = async () => {
    if (!importUrl.trim()) {
      toast.error("Please enter a URL");
      return;
    }

    setImportLoading(true);
    setPreviewData(null);

    try {
      const response = await axios.post(`${API}/scrape/game`, { url: importUrl });
      setPreviewData(response.data);
      toast.success("Game info retrieved!");
    } catch (error) {
      console.error("Failed to scrape:", error);
      toast.error(error.response?.data?.detail || "Failed to fetch game info from URL");
    } finally {
      setImportLoading(false);
    }
  };

  const handleImportGame = async () => {
    if (!importUrl.trim()) {
      toast.error("Please enter a URL");
      return;
    }

    setImportLoading(true);

    try {
      const response = await axios.post(`${API}/scrape/import-game`, { url: importUrl });
      toast.success(`Imported "${response.data.game.name}" with ${response.data.achievementsImported} achievements!`);
      setImportDialogOpen(false);
      setImportUrl("");
      setPreviewData(null);
      fetchGames();
    } catch (error) {
      console.error("Failed to import:", error);
      toast.error(error.response?.data?.detail || "Failed to import game");
    } finally {
      setImportLoading(false);
    }
  };

  // IGDB Search Functions
  const handleIgdbSearch = async () => {
    if (!igdbSearch.trim()) {
      toast.error("Please enter a search term");
      return;
    }

    setIgdbLoading(true);
    setIgdbResults([]);
    setSelectedIgdbGame(null);

    try {
      const response = await axios.get(`${API}/igdb/search`, {
        params: { query: igdbSearch, limit: 50 }
      });
      setIgdbResults(response.data.results || []);
      if (response.data.results?.length === 0) {
        toast.info("No games found");
      }
    } catch (error) {
      console.error("IGDB search failed:", error);
      toast.error(error.response?.data?.detail || "Search failed");
    } finally {
      setIgdbLoading(false);
    }
  };

  const handleSelectIgdbGame = async (game) => {
    setIgdbLoading(true);
    try {
      const response = await axios.get(`${API}/igdb/game/${game.id}`);
      setSelectedIgdbGame(response.data);
      // Auto-select first available platform
      if (response.data.platforms?.length > 0) {
        const firstPlatform = response.data.platforms[0];
        setIgdbImportPlatform(firstPlatform.our_platform);
        setIgdbImportSystem(firstPlatform.our_system);
      }
    } catch (error) {
      console.error("Failed to get game details:", error);
      toast.error("Failed to load game details");
    } finally {
      setIgdbLoading(false);
    }
  };

  const handleIgdbImport = async () => {
    if (!selectedIgdbGame || !igdbImportPlatform || !igdbImportSystem) {
      toast.error("Please select a platform and system");
      return;
    }

    setIgdbLoading(true);

    try {
      const response = await axios.post(`${API}/igdb/import`, {
        igdb_id: selectedIgdbGame.id,
        platform: igdbImportPlatform,
        system: igdbImportSystem,
      });
      toast.success(`Imported "${response.data.game.name}" successfully!`);
      setImportDialogOpen(false);
      setIgdbSearch("");
      setIgdbResults([]);
      setSelectedIgdbGame(null);
      setIgdbImportPlatform("");
      setIgdbImportSystem("");
      fetchGames();
    } catch (error) {
      console.error("IGDB import failed:", error);
      toast.error(error.response?.data?.detail || "Failed to import game");
    } finally {
      setIgdbLoading(false);
    }
  };

  // Xbox/OpenXBL Search Functions
  useEffect(() => {
    // Check if Xbox gamertag is configured
    const checkXboxStatus = async () => {
      try {
        const response = await axios.get(`${API}/xbox/status`);
        setXboxGamertagConfigured(response.data.configured && response.data.gamertag);
      } catch (error) {
        console.error("Failed to check Xbox status:", error);
      }
    };
    checkXboxStatus();
  }, []);

  const handleXboxSearch = async () => {
    if (!xboxSearch.trim()) {
      toast.error("Please enter a game name");
      return;
    }

    setXboxLoading(true);
    setXboxResults([]);
    setSelectedXboxGame(null);

    try {
      const response = await axios.post(`${API}/xbox/search-title?query=${encodeURIComponent(xboxSearch)}`);
      setXboxResults(response.data.results || []);
      if (response.data.results?.length === 0) {
        toast.info("No Xbox games found");
      }
    } catch (error) {
      console.error("Xbox search failed:", error);
      toast.error(error.response?.data?.detail || "Search failed");
    } finally {
      setXboxLoading(false);
    }
  };

  const handleSelectXboxGame = (game) => {
    setSelectedXboxGame(game);
    // Auto-select first Xbox system if available
    const xboxPlatform = platforms.find(p => p.name.toLowerCase() === 'xbox');
    if (xboxPlatform) {
      const xboxSystems = systems[xboxPlatform.id] || [];
      if (xboxSystems.length > 0) {
        setXboxImportSystem(xboxSystems[0].name);
      }
    }
  };

  const handleXboxImport = async () => {
    if (!selectedXboxGame) {
      toast.error("Please select a game");
      return;
    }

    setXboxLoading(true);

    try {
      const response = await axios.post(`${API}/xbox/import-game`, {
        title_id: selectedXboxGame.titleId,
        name: selectedXboxGame.name,
        image_url: selectedXboxGame.imageUrl,
        system: xboxImportSystem === "none" ? "" : xboxImportSystem,
      });
      
      toast.success(`Imported "${response.data.game.name}" with ${response.data.achievementsImported} achievements!`);
      setImportDialogOpen(false);
      setXboxSearch("");
      setXboxResults([]);
      setSelectedXboxGame(null);
      setXboxImportSystem("");
      fetchGames();
    } catch (error) {
      console.error("Xbox import failed:", error);
      // Handle Pydantic validation errors (array of error objects)
      const errorDetail = error.response?.data?.detail;
      if (Array.isArray(errorDetail)) {
        const errorMessages = errorDetail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
        toast.error(errorMessages || "Failed to import game");
      } else {
        toast.error(errorDetail || "Failed to import game");
      }
    } finally {
      setXboxLoading(false);
    }
  };

  // Steam search handlers
  const handleSteamSearch = async () => {
    if (!steamSearch.trim()) {
      toast.error("Please enter a game name");
      return;
    }

    setSteamLoading(true);
    setSteamResults([]);
    setSelectedSteamGame(null);

    try {
      const response = await axios.post(`${API}/steam/search-games`, { query: steamSearch });
      setSteamResults(response.data || []);
      if (response.data?.length === 0) {
        toast.info("No Steam games found");
      }
    } catch (error) {
      console.error("Steam search failed:", error);
      toast.error(error.response?.data?.detail || "Search failed");
    } finally {
      setSteamLoading(false);
    }
  };

  const handleSelectSteamGame = (game) => {
    setSelectedSteamGame(game);
  };

  const handleSteamImport = async () => {
    if (!selectedSteamGame) {
      toast.error("Please select a game");
      return;
    }

    setSteamLoading(true);

    try {
      const response = await axios.post(`${API}/steam/import-game`, {
        appId: selectedSteamGame.appId,
        name: selectedSteamGame.name,
        system: "PC",
      });
      
      const achCount = response.data.achievementsImported + response.data.achievementsUpdated;
      toast.success(`Imported "${selectedSteamGame.name}" with ${achCount} achievements!`);
      setImportDialogOpen(false);
      setSteamSearch("");
      setSteamResults([]);
      setSelectedSteamGame(null);
      fetchGames();
    } catch (error) {
      console.error("Steam import failed:", error);
      // Handle Pydantic validation errors (array) vs regular errors (string)
      const errorDetail = error.response?.data?.detail;
      let errorMessage = "Failed to import game";
      if (typeof errorDetail === 'string') {
        errorMessage = errorDetail;
      } else if (Array.isArray(errorDetail)) {
        errorMessage = errorDetail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
      }
      toast.error(errorMessage);
    } finally {
      setSteamLoading(false);
    }
  };

  // PlayStation search handlers
  const handlePsnSearch = async () => {
    if (!psnSearch.trim()) {
      toast.error("Please enter a game name");
      return;
    }

    setPsnLoading(true);
    setPsnResults([]);
    setSelectedPsnGame(null);

    try {
      const response = await axios.post(`${API}/playstation/search-games`, { query: psnSearch });
      setPsnResults(response.data || []);
      if (response.data?.length === 0) {
        toast.info("No PlayStation games found");
      }
    } catch (error) {
      console.error("PlayStation search failed:", error);
      toast.error(error.response?.data?.detail || "Search failed");
    } finally {
      setPsnLoading(false);
    }
  };

  const handleSelectPsnGame = (game) => {
    setSelectedPsnGame(game);
  };

  const handlePsnImport = async () => {
    if (!selectedPsnGame) {
      toast.error("Please select a game");
      return;
    }

    setPsnLoading(true);

    try {
      const response = await axios.post(`${API}/playstation/import-game`, {
        np_communication_id: selectedPsnGame.npCommunicationId,
        name: selectedPsnGame.name,
        system: selectedPsnGame.system || "",
        image_url: selectedPsnGame.imageUrl || "",
      });
      
      const trophyCount = response.data.trophiesImported + response.data.trophiesUpdated;
      toast.success(`Imported "${selectedPsnGame.name}" with ${trophyCount} trophies!`);
      setImportDialogOpen(false);
      setPsnSearch("");
      setPsnResults([]);
      setSelectedPsnGame(null);
      fetchGames();
    } catch (error) {
      console.error("PlayStation import failed:", error);
      toast.error(error.response?.data?.detail || "Failed to import game");
    } finally {
      setPsnLoading(false);
    }
  };

  // Handle Epic Games manual add
  const handleAddEpicGame = async () => {
    if (!epicGameName.trim()) {
      toast.error("Please enter a game name");
      return;
    }

    setEpicAddLoading(true);

    try {
      const response = await axios.post(`${API}/epic/add-game`, {
        name: epicGameName.trim(),
        system: "PC",
        boxArtUrl: epicBoxArtUrl.trim() || null,
        isOwned: true,
      });
      
      if (response.data.created) {
        toast.success(`Added "${epicGameName}" to your Epic Games library!`);
      } else {
        toast.info(`"${epicGameName}" already exists in your library`);
      }
      
      setImportDialogOpen(false);
      setEpicGameName("");
      setEpicBoxArtUrl("");
      fetchGames();
    } catch (error) {
      console.error("Epic game add failed:", error);
      toast.error(error.response?.data?.detail || "Failed to add game");
    } finally {
      setEpicAddLoading(false);
    }
  };

  const getCompletionColor = (percentage) => {
    if (percentage === 100) return "bg-green-500";
    if (percentage >= 50) return "bg-yellow-500";
    return "bg-purple-500";
  };

  const getPlatformSystems = (platformId) => {
    return systems[platformId] || [];
  };

  const getPlatformColor = (platformId) => {
    const platform = platforms.find((p) => p.id === platformId);
    return platform?.color || "#A1A1AA";
  };

  const getScoreAbbr = (platformId) => {
    const platform = platforms.find((p) => p.id === platformId);
    return platform?.scoreAbbr || "XP";
  };

  // Check if a game has incomplete sync (Xbox games where API couldn't fetch all achievements OR manually flagged)
  const hasIncompleteSync = (game) => {
    // First check if manually marked as incomplete sync
    if (game.isIncompleteSync) return true;
    
    // Only Xbox games can have auto-detected incomplete sync
    const xboxPlatform = platforms.find(p => p.name.toLowerCase() === 'xbox');
    if (!xboxPlatform || game.platform !== xboxPlatform.id) return false;
    
    // Check if Xbox reported more achievements than we have
    if (game.xboxTotalAchievements && game.xboxTotalAchievements > 0) {
      return game.totalAchievements < game.xboxTotalAchievements;
    }
    return false;
  };

  // Generate TrueAchievements URL from game name
  const getTrueAchievementsUrl = (gameName) => {
    // TrueAchievements URL format: https://www.trueachievements.com/game/Game-Name/achievements
    const slug = gameName
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
    return `https://www.trueachievements.com/game/${slug}/achievements`;
  };

  // Handle incomplete sync icon click
  const handleIncompleteSyncClick = (e, game) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedIncompleteGame(game);
    setIncompleteSyncDialogOpen(true);
  };

  // Try to auto-sync missing achievements from Xbox API
  const handleAutoSyncAttempt = async (game) => {
    if (!game) return;
    
    setAutoSyncLoading(true);
    try {
      const response = await axios.post(`${API}/xbox/game/${game.id}/sync-achievements`);
      const { added, updated } = response.data;
      
      if (added > 0 || updated > 0) {
        toast.success(`Synced ${added} new achievements, updated ${updated} existing`);
        setIncompleteSyncDialogOpen(false);
        fetchGames();
      } else {
        toast.info("No new achievements found. The API may not have data for this title. Try manual entry instead.");
      }
    } catch (error) {
      console.error("Auto-sync failed:", error);
      const message = error.response?.data?.detail || "Failed to sync achievements";
      toast.error(message);
      
      // If it's an API limitation, suggest manual entry
      if (message.includes("Xbox 360") || message.includes("API") || message.includes("not found")) {
        toast.info("Try the manual entry option below");
      }
    } finally {
      setAutoSyncLoading(false);
    }
  };

  // Parse bulk achievement text into structured data
  const parseBulkAchievements = (text) => {
    if (!text.trim()) return [];
    
    const lines = text.trim().split('\n');
    return lines
      .map(line => {
        const parts = line.split('|').map(p => p.trim());
        const name = parts[0] || '';
        const description = parts[1] || '';
        const score = parseInt(parts[2]) || 0;
        
        return name ? { name, description, score } : null;
      })
      .filter(Boolean);
  };

  // Handle bulk add achievements
  const handleBulkAddAchievements = async () => {
    if (!manualEntryGame || !bulkAchievementText.trim()) return;
    
    const achievements = parseBulkAchievements(bulkAchievementText);
    if (achievements.length === 0) {
      toast.error("No valid achievements found. Check the format.");
      return;
    }
    
    setBulkAddLoading(true);
    try {
      const response = await axios.post(`${API}/achievements/bulk`, {
        gameId: manualEntryGame.id,
        achievements: achievements.map(a => ({
          name: a.name,
          description: a.description,
          score: a.score,
          isUnlocked: false,
        }))
      });
      
      const { added, skipped } = response.data;
      toast.success(`Added ${added} achievements${skipped > 0 ? `, skipped ${skipped} duplicates` : ''}`);
      setManualEntryDialogOpen(false);
      setBulkAchievementText("");
      setManualEntryGame(null);
      fetchGames();
    } catch (error) {
      console.error("Bulk add failed:", error);
      toast.error(error.response?.data?.detail || "Failed to add achievements");
    } finally {
      setBulkAddLoading(false);
    }
  };

  const handleToggleBacklog = async (e, game) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const response = await axios.post(`${API}/games/${game.id}/backlog`);
      toast.success(response.data.message);
      fetchGames();
    } catch (error) {
      console.error("Failed to toggle backlog:", error);
      toast.error("Failed to update backlog status");
    }
  };

  return (
    <div data-testid="games-page" className="space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading font-bold text-3xl md:text-4xl text-white tracking-tight">
            Games Library
          </h1>
          <p className="text-white/50 mt-1">{games.length} games in your collection</p>
        </div>

        <div className="flex gap-2">
          {/* Bulk Edit Button */}
          {!selectionMode ? (
            <Button
              data-testid="edit-games-btn"
              variant="outline"
              onClick={enterSelectionMode}
              className="border-white/20 text-white hover:bg-white/10"
            >
              <Edit2 className="w-4 h-4 mr-2" />
              Edit Games
            </Button>
          ) : (
            <div className="flex items-center gap-2">
              <span className="text-sm text-white/60">
                {selectedGameIds.length} game{selectedGameIds.length !== 1 ? 's' : ''} selected
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={selectAllOnPage}
                className="border-white/20 text-white hover:bg-white/10"
              >
                {displayedGames.every(g => selectedGameIds.includes(g.id)) ? 'Deselect Page' : 'Select Page'}
              </Button>
              <Button
                variant="outline"
                onClick={exitSelectionMode}
                className="border-white/20 text-white hover:bg-white/10"
              >
                Cancel
              </Button>
              <Button
                data-testid="bulk-delete-btn"
                variant="outline"
                onClick={() => setBulkDeleteDialogOpen(true)}
                disabled={selectedGameIds.length === 0}
                className="border-red-500/50 text-red-400 hover:bg-red-500/10 disabled:opacity-50"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete ({selectedGameIds.length})
              </Button>
              <Button
                data-testid="bulk-edit-ok-btn"
                onClick={startBulkEdit}
                disabled={selectedGameIds.length === 0}
                className="bg-green-600 hover:bg-green-500 text-white disabled:opacity-50"
              >
                <Check className="w-4 h-4 mr-2" />
                OK ({selectedGameIds.length})
              </Button>
            </div>
          )}
          
          {/* Import Game Button */}
          <Dialog open={importDialogOpen} onOpenChange={(open) => {
            setImportDialogOpen(open);
            if (!open) {
              // Reset all states when closing
              setImportUrl("");
              setPreviewData(null);
              setIgdbSearch("");
              setIgdbResults([]);
              setSelectedIgdbGame(null);
              setIgdbImportPlatform("");
              setIgdbImportSystem("");
              setEpicGameName("");
              setEpicBoxArtUrl("");
            }
          }}>
            <DialogTrigger asChild>
              <Button
                data-testid="import-url-btn"
                variant="outline"
                className="border-white/20 text-white hover:bg-white/10"
              >
                <Database className="w-4 h-4 mr-2" />
                Import Game
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl">Import Game</DialogTitle>
              </DialogHeader>
              
              <Tabs defaultValue="igdb" className="mt-4">
                <div className="overflow-x-auto -mx-4 px-4 pb-2">
                  <TabsList className="bg-[#121212] border border-white/5 inline-flex min-w-full sm:grid sm:grid-cols-6 gap-1">
                    <TabsTrigger value="igdb" className="data-[state=active]:bg-white/10 whitespace-nowrap px-3 py-2 flex-1 sm:flex-none">
                      <Database className="w-4 h-4 mr-1 shrink-0" />
                      <span className="hidden sm:inline">IGDB</span>
                    </TabsTrigger>
                    <TabsTrigger value="xbox" className="data-[state=active]:bg-white/10 whitespace-nowrap px-3 py-2 flex-1 sm:flex-none">
                      <img src="/platform-logos/xbox-green.png" alt="Xbox" className="w-4 h-4 mr-1 object-contain shrink-0" />
                      <span className="hidden sm:inline">Xbox</span>
                    </TabsTrigger>
                    <TabsTrigger value="steam" className="data-[state=active]:bg-white/10 whitespace-nowrap px-3 py-2 flex-1 sm:flex-none">
                      <img src="/platform-logos/steam.ico" alt="Steam" className="w-4 h-4 mr-1 shrink-0" />
                      <span className="hidden sm:inline">Steam</span>
                    </TabsTrigger>
                    <TabsTrigger value="playstation" className="data-[state=active]:bg-white/10 whitespace-nowrap px-3 py-2 flex-1 sm:flex-none">
                      <img src="/platform-logos/playstation.png" alt="PlayStation" className="w-4 h-4 mr-1 object-contain shrink-0" />
                      <span className="hidden sm:inline">PSN</span>
                    </TabsTrigger>
                    <TabsTrigger value="epic" className="data-[state=active]:bg-white/10 whitespace-nowrap px-3 py-2 flex-1 sm:flex-none">
                      <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/3/31/Epic_Games_logo.svg/1200px-Epic_Games_logo.svg.png" alt="Epic" className="w-4 h-4 mr-1 invert object-contain shrink-0" />
                      <span className="hidden sm:inline">Epic</span>
                    </TabsTrigger>
                    <TabsTrigger value="url" className="data-[state=active]:bg-white/10 whitespace-nowrap px-3 py-2 flex-1 sm:flex-none">
                      <Globe className="w-4 h-4 mr-1 shrink-0" />
                      <span className="hidden sm:inline">URL</span>
                    </TabsTrigger>
                  </TabsList>
                </div>
                
                {/* IGDB Search Tab */}
                <TabsContent value="igdb" className="space-y-4 mt-4">
                  <div className="p-3 bg-purple-500/10 border border-purple-500/20 rounded-lg">
                    <p className="text-purple-400 text-sm flex items-center gap-2">
                      <Star className="w-4 h-4" />
                      Search IGDB - The Internet Game Database (Twitch/Amazon)
                    </p>
                  </div>
                  
                  {/* Search Input */}
                  <div className="space-y-2">
                    <Label>Search Game</Label>
                    <div className="flex gap-2">
                      <Input
                        data-testid="igdb-search-input"
                        placeholder="Enter game name..."
                        value={igdbSearch}
                        onChange={(e) => setIgdbSearch(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && handleIgdbSearch()}
                        className="bg-black/50 border-white/10 flex-1"
                      />
                      <Button
                        onClick={handleIgdbSearch}
                        disabled={igdbLoading}
                        className="bg-purple-600 hover:bg-purple-700"
                      >
                        {igdbLoading ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Search className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                  
                  {/* Search Results */}
                  {igdbResults.length > 0 && !selectedIgdbGame && (
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      <Label className="text-white/60">Search Results ({igdbResults.length} found)</Label>
                      {igdbResults.map((game) => (
                        <button
                          key={game.id}
                          onClick={() => handleSelectIgdbGame(game)}
                          className="w-full p-3 bg-[#121212] rounded-lg border border-white/5 hover:border-purple-500/40 transition-colors text-left flex gap-3"
                        >
                          {game.cover_url ? (
                            <img
                              src={game.cover_url}
                              alt={game.name}
                              className="w-16 h-20 object-contain rounded bg-black/50"
                            />
                          ) : (
                            <div className="w-16 h-20 bg-white/5 rounded flex items-center justify-center">
                              <Gamepad2 className="w-6 h-6 text-white/20" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-white font-medium truncate">{game.name}</p>
                            <p className="text-white/50 text-sm">{game.released || "Unknown release"}</p>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {game.genres?.slice(0, 2).map((g, i) => (
                                <span key={i} className="text-xs px-1.5 py-0.5 bg-purple-500/20 text-purple-300 rounded">
                                  {g}
                                </span>
                              ))}
                              {game.rating && (
                                <span className="text-xs px-1.5 py-0.5 bg-yellow-500/20 text-yellow-300 rounded">
                                  {game.rating}%
                                </span>
                              )}
                            </div>
                            <p className="text-white/40 text-xs mt-1 truncate">
                              {game.developers?.join(", ") || "Unknown developer"}
                            </p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                  
                  {/* Selected Game Details */}
                  {selectedIgdbGame && (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <Label className="text-white/60">Selected Game</Label>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setSelectedIgdbGame(null)}
                          className="text-white/40 hover:text-white h-auto py-1"
                        >
                          Change
                        </Button>
                      </div>
                      <div className="p-4 bg-[#121212] rounded-lg border border-purple-500/30 space-y-3">
                        <div className="flex gap-4">
                          {selectedIgdbGame.cover_url ? (
                            <img
                              src={selectedIgdbGame.cover_url}
                              alt={selectedIgdbGame.name}
                              className="w-24 h-32 object-contain rounded bg-black/50"
                            />
                          ) : (
                            <div className="w-24 h-32 bg-white/5 rounded flex items-center justify-center">
                              <Gamepad2 className="w-8 h-8 text-white/20" />
                            </div>
                          )}
                          <div className="flex-1 space-y-1">
                            <p className="text-white font-medium text-lg">{selectedIgdbGame.name}</p>
                            <p className="text-white/50 text-sm">{selectedIgdbGame.released || "Unknown release"}</p>
                            {selectedIgdbGame.developers?.length > 0 && (
                              <p className="text-white/40 text-xs">Developer: {selectedIgdbGame.developers.join(", ")}</p>
                            )}
                            {selectedIgdbGame.publishers?.length > 0 && (
                              <p className="text-white/40 text-xs">Publisher: {selectedIgdbGame.publishers.join(", ")}</p>
                            )}
                            {selectedIgdbGame.genres?.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-1">
                                {selectedIgdbGame.genres.map((g, i) => (
                                  <span key={i} className="text-xs px-1.5 py-0.5 bg-purple-500/20 text-purple-300 rounded">
                                    {g}
                                  </span>
                                ))}
                              </div>
                            )}
                            {selectedIgdbGame.rating && (
                              <p className="text-yellow-400 text-sm">User Rating: {selectedIgdbGame.rating}%</p>
                            )}
                          </div>
                        </div>
                        
                        {/* Platform Selection */}
                        <div className="pt-3 border-t border-white/10 space-y-3">
                          <Label>Select Platform & System</Label>
                          <PlatformSystemPicker
                            idPrefix="igdb"
                            platforms={platforms}
                            systemsForPlatform={getPlatformSystems(igdbImportPlatform)}
                            platform={igdbImportPlatform}
                            system={igdbImportSystem}
                            onPlatformChange={(v) => {
                              setIgdbImportPlatform(v);
                              const matchingPlatform = selectedIgdbGame.platforms?.find(p => p.our_platform === v);
                              setIgdbImportSystem(matchingPlatform ? matchingPlatform.our_system : "");
                            }}
                            onSystemChange={setIgdbImportSystem}
                            onDataRefresh={fetchPlatformsAndSystems}
                          />
                          
                          {selectedIgdbGame.platforms?.length > 0 && (
                            <p className="text-white/40 text-xs">
                              Available on: {selectedIgdbGame.platforms.map(p => p.name).join(", ")}
                            </p>
                          )}
                        </div>
                      </div>
                      
                      <Button
                        onClick={handleIgdbImport}
                        disabled={igdbLoading || !igdbImportPlatform || !igdbImportSystem}
                        data-testid="igdb-import-btn"
                        className="w-full bg-purple-600 hover:bg-purple-700 font-bold uppercase tracking-wider"
                      >
                        {igdbLoading ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Importing...
                          </>
                        ) : (
                          <>
                            <Database className="w-4 h-4 mr-2" />
                            Import from IGDB
                          </>
                        )}
                      </Button>
                    </div>
                  )}
                </TabsContent>
                
                {/* Xbox Import Tab */}
                <TabsContent value="xbox" className="space-y-4 mt-4">
                  <div className="p-3 bg-[#107C10]/10 border border-[#107C10]/20 rounded-lg">
                    <p className="text-[#107C10] text-sm flex items-center gap-2">
                      <img src="/platform-logos/xbox-green.png" alt="Xbox" className="w-4 h-4 object-contain" />
                      Search Xbox Live - Import games with all achievements
                    </p>
                  </div>
                  
                  {/* Search Input */}
                  <div className="space-y-2">
                    <Label>Search Xbox Game</Label>
                    <div className="flex gap-2">
                      <Input
                        data-testid="xbox-search-input"
                        placeholder="Enter game name..."
                        value={xboxSearch}
                        onChange={(e) => setXboxSearch(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && handleXboxSearch()}
                        className="bg-black/50 border-white/10 flex-1"
                      />
                      <Button
                        onClick={handleXboxSearch}
                        disabled={xboxLoading}
                        className="bg-[#107C10] hover:bg-[#0e6b0e]"
                      >
                        {xboxLoading ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Search className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                  
                {/* Search Results */}
                  {xboxResults.length > 0 && !selectedXboxGame && (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      <Label className="text-white/60">Search Results</Label>
                      {xboxResults.map((game) => (
                        <button
                          key={game.titleId}
                          onClick={() => handleSelectXboxGame(game)}
                          className="w-full flex items-center gap-3 p-2 bg-[#121212] hover:bg-white/10 rounded-lg transition-colors text-left"
                        >
                          {game.imageUrl ? (
                            <img
                              src={game.imageUrl}
                              alt={game.name}
                              className="w-12 h-12 object-contain rounded bg-black/50"
                            />
                          ) : (
                            <div className="w-12 h-12 bg-[#107C10]/20 rounded flex items-center justify-center">
                              <Gamepad2 className="w-6 h-6 text-[#107C10]" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-white font-medium truncate">{game.name}</p>
                            <p className="text-white/40 text-xs">Title ID: {game.titleId}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                  
                  {/* No Results Message */}
                  {xboxResults.length === 0 && xboxSearch && !xboxLoading && !selectedXboxGame && (
                    <div className="p-4 bg-white/5 rounded-lg border border-white/10 text-center">
                      <p className="text-white/60 text-sm mb-2">No games found for &quot;{xboxSearch}&quot;</p>
                      <p className="text-white/40 text-xs">
                        Tip: First sync your Xbox profile in Settings to search your games. 
                        Rate limits apply (150 requests/hour).
                      </p>
                    </div>
                  )}
                  
                  {/* Selected Game */}
                  {selectedXboxGame && (
                    <div className="space-y-4">
                      <div className="flex items-start gap-4 p-4 bg-[#121212] rounded-lg border border-[#107C10]/30">
                        {selectedXboxGame.imageUrl ? (
                          <img
                            src={selectedXboxGame.imageUrl}
                            alt={selectedXboxGame.name}
                            className="w-20 h-20 object-contain rounded bg-black/50"
                          />
                        ) : (
                          <div className="w-20 h-20 bg-[#107C10]/20 rounded flex items-center justify-center">
                            <Gamepad2 className="w-10 h-10 text-[#107C10]" />
                          </div>
                        )}
                        <div className="flex-1">
                          <h3 className="text-white font-bold text-lg">{selectedXboxGame.name}</h3>
                          <p className="text-white/50 text-sm">Xbox Title ID: {selectedXboxGame.titleId}</p>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setSelectedXboxGame(null)}
                            className="text-white/50 hover:text-white mt-2 p-0 h-auto"
                          >
                            ← Back to search results
                          </Button>
                        </div>
                      </div>
                      
                      {/* System Selection */}
                      <div className="space-y-2">
                        <Label>System (Optional)</Label>
                        <Select value={xboxImportSystem} onValueChange={setXboxImportSystem}>
                          <SelectTrigger className="bg-black/50 border-white/10">
                            <SelectValue placeholder="Select system..." />
                          </SelectTrigger>
                          <SelectContent className="bg-[#1A1A1A] border-white/10">
                            {platforms
                              .filter(p => p.name.toLowerCase() === 'xbox')
                              .map(platform => (
                                systems[platform.id]?.map(system => (
                                  <SelectItem key={system.id} value={system.name}>
                                    {system.name}
                                  </SelectItem>
                                ))
                              ))}
                            <SelectItem value="none">No specific system</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {/* Import Button */}
                      <Button
                        onClick={handleXboxImport}
                        disabled={xboxLoading}
                        className="w-full bg-[#107C10] hover:bg-[#0e6b0e]"
                      >
                        {xboxLoading ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Importing...
                          </>
                        ) : (
                          <>
                            <Download className="w-4 h-4 mr-2" />
                            Import Game & Achievements
                          </>
                        )}
                      </Button>
                    </div>
                  )}
                </TabsContent>
                
                {/* Steam Import Tab */}
                <TabsContent value="steam" className="space-y-4 mt-4">
                  <div className="p-3 bg-[#1b2838]/30 border border-[#66C0F4]/20 rounded-lg">
                    <p className="text-[#66C0F4] text-sm flex items-center gap-2">
                      <img src="/platform-logos/steam.ico" alt="Steam" className="w-4 h-4" />
                      Search Steam - Import games with all achievements
                    </p>
                  </div>
                  
                  {/* Search Input */}
                  <div className="space-y-2">
                    <Label>Search Steam Game</Label>
                    <div className="flex gap-2">
                      <Input
                        data-testid="steam-search-input"
                        placeholder="Enter game name..."
                        value={steamSearch}
                        onChange={(e) => setSteamSearch(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && handleSteamSearch()}
                        className="bg-black/50 border-white/10 flex-1"
                      />
                      <Button
                        onClick={handleSteamSearch}
                        disabled={steamLoading}
                        className="bg-[#1b2838] hover:bg-[#2a475e]"
                      >
                        {steamLoading ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Search className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                  
                  {/* Search Results */}
                  {steamResults.length > 0 && !selectedSteamGame && (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      <Label className="text-white/60">Search Results</Label>
                      {steamResults.map((game) => (
                        <button
                          key={game.appId}
                          onClick={() => handleSelectSteamGame(game)}
                          className="w-full flex items-center gap-3 p-2 bg-[#121212] hover:bg-white/10 rounded-lg transition-colors text-left"
                        >
                          {game.imageUrl ? (
                            <img
                              src={game.imageUrl}
                              alt={game.name}
                              className="w-12 h-16 object-contain rounded bg-black/50"
                            />
                          ) : (
                            <div className="w-12 h-16 bg-[#66C0F4]/20 rounded flex items-center justify-center">
                              <Gamepad2 className="w-6 h-6 text-[#66C0F4]" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-white font-medium truncate">{game.name}</p>
                            <p className="text-white/40 text-xs">
                              App ID: {game.appId}
                              {game.owned && <span className="ml-2 text-[#66C0F4]">• Owned</span>}
                            </p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                  
                  {/* No Results */}
                  {steamResults.length === 0 && steamSearch && !steamLoading && !selectedSteamGame && (
                    <div className="p-4 bg-white/5 rounded-lg border border-white/10 text-center">
                      <p className="text-white/60 text-sm mb-2">No games found for &quot;{steamSearch}&quot;</p>
                      <p className="text-white/40 text-xs">
                        Tip: First sync your Steam profile in Settings to search your games.
                      </p>
                    </div>
                  )}
                  
                  {/* Selected Game */}
                  {selectedSteamGame && (
                    <div className="space-y-4">
                      <div className="flex items-start gap-4 p-4 bg-[#121212] rounded-lg border border-[#66C0F4]/30">
                        {selectedSteamGame.imageUrl ? (
                          <img
                            src={selectedSteamGame.imageUrl}
                            alt={selectedSteamGame.name}
                            className="w-16 h-20 object-contain rounded bg-black/50"
                          />
                        ) : (
                          <div className="w-16 h-20 bg-[#66C0F4]/20 rounded flex items-center justify-center">
                            <Gamepad2 className="w-8 h-8 text-[#66C0F4]" />
                          </div>
                        )}
                        <div className="flex-1">
                          <h3 className="text-white font-bold text-lg">{selectedSteamGame.name}</h3>
                          <p className="text-white/50 text-sm">Steam App ID: {selectedSteamGame.appId}</p>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setSelectedSteamGame(null)}
                            className="text-white/50 hover:text-white mt-2 p-0 h-auto"
                          >
                            ← Back to search results
                          </Button>
                        </div>
                      </div>
                      
                      <Button
                        onClick={handleSteamImport}
                        disabled={steamLoading}
                        className="w-full bg-[#1b2838] hover:bg-[#2a475e]"
                      >
                        {steamLoading ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Importing...
                          </>
                        ) : (
                          <>
                            <Download className="w-4 h-4 mr-2" />
                            Import Game & Achievements
                          </>
                        )}
                      </Button>
                    </div>
                  )}
                </TabsContent>
                
                {/* PlayStation Import Tab */}
                <TabsContent value="playstation" className="space-y-4 mt-4">
                  <div className="p-3 bg-[#003087]/10 border border-[#003087]/20 rounded-lg">
                    <p className="text-[#4a90d9] text-sm flex items-center gap-2">
                      <img src="/platform-logos/playstation.png" alt="PlayStation" className="w-4 h-4 object-contain" />
                      Search PlayStation - Import games with all trophies
                    </p>
                  </div>
                  
                  {/* Search Input */}
                  <div className="space-y-2">
                    <Label>Search PlayStation Game</Label>
                    <div className="flex gap-2">
                      <Input
                        data-testid="psn-search-input"
                        placeholder="Enter game name..."
                        value={psnSearch}
                        onChange={(e) => setPsnSearch(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && handlePsnSearch()}
                        className="bg-black/50 border-white/10 flex-1"
                      />
                      <Button
                        onClick={handlePsnSearch}
                        disabled={psnLoading}
                        className="bg-[#003087] hover:bg-[#002060]"
                      >
                        {psnLoading ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Search className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                  
                  {/* Search Results */}
                  {psnResults.length > 0 && !selectedPsnGame && (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      <Label className="text-white/60">Search Results</Label>
                      {psnResults.map((game) => (
                        <button
                          key={game.npCommunicationId}
                          onClick={() => handleSelectPsnGame(game)}
                          className="w-full flex items-center gap-3 p-2 bg-[#121212] hover:bg-white/10 rounded-lg transition-colors text-left"
                        >
                          {game.imageUrl ? (
                            <img
                              src={game.imageUrl}
                              alt={game.name}
                              className="w-12 h-12 object-contain rounded bg-black/50"
                            />
                          ) : (
                            <div className="w-12 h-12 bg-[#003087]/20 rounded flex items-center justify-center">
                              <Gamepad2 className="w-6 h-6 text-[#4a90d9]" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-white font-medium truncate">{game.name}</p>
                            <p className="text-white/40 text-xs">{game.system} • {game.progress}% complete</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                  
                  {/* No Results */}
                  {psnResults.length === 0 && psnSearch && !psnLoading && !selectedPsnGame && (
                    <div className="p-4 bg-white/5 rounded-lg border border-white/10 text-center">
                      <p className="text-white/60 text-sm mb-2">No games found for &quot;{psnSearch}&quot;</p>
                      <p className="text-white/40 text-xs">
                        Tip: First sync your PlayStation profile in Settings to search your games.
                      </p>
                    </div>
                  )}
                  
                  {/* Selected Game */}
                  {selectedPsnGame && (
                    <div className="space-y-4">
                      <div className="flex items-start gap-4 p-4 bg-[#121212] rounded-lg border border-[#003087]/30">
                        {selectedPsnGame.imageUrl ? (
                          <img
                            src={selectedPsnGame.imageUrl}
                            alt={selectedPsnGame.name}
                            className="w-16 h-16 object-contain rounded bg-black/50"
                          />
                        ) : (
                          <div className="w-16 h-16 bg-[#003087]/20 rounded flex items-center justify-center">
                            <Gamepad2 className="w-8 h-8 text-[#4a90d9]" />
                          </div>
                        )}
                        <div className="flex-1">
                          <h3 className="text-white font-bold text-lg">{selectedPsnGame.name}</h3>
                          <p className="text-white/50 text-sm">{selectedPsnGame.system}</p>
                          <div className="flex gap-2 mt-1 text-xs text-white/40">
                            <span>🥉 {selectedPsnGame.earnedTrophies?.bronze || 0}/{selectedPsnGame.definedTrophies?.bronze || 0}</span>
                            <span>🥈 {selectedPsnGame.earnedTrophies?.silver || 0}/{selectedPsnGame.definedTrophies?.silver || 0}</span>
                            <span>🥇 {selectedPsnGame.earnedTrophies?.gold || 0}/{selectedPsnGame.definedTrophies?.gold || 0}</span>
                            <span>🏆 {selectedPsnGame.earnedTrophies?.platinum || 0}/{selectedPsnGame.definedTrophies?.platinum || 0}</span>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setSelectedPsnGame(null)}
                            className="text-white/50 hover:text-white mt-2 p-0 h-auto"
                          >
                            ← Back to search results
                          </Button>
                        </div>
                      </div>
                      
                      <Button
                        onClick={handlePsnImport}
                        disabled={psnLoading}
                        className="w-full bg-[#003087] hover:bg-[#002060]"
                      >
                        {psnLoading ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Importing...
                          </>
                        ) : (
                          <>
                            <Download className="w-4 h-4 mr-2" />
                            Import Game & Trophies
                          </>
                        )}
                      </Button>
                    </div>
                  )}
                </TabsContent>
                
                {/* Epic Games Tab */}
                <TabsContent value="epic" className="space-y-4 mt-4">
                  <div className="p-3 bg-white/5 border border-white/10 rounded-lg">
                    <p className="text-white/70 text-sm flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-yellow-500" />
                      Epic Games doesn&apos;t have a public API. Add games manually below.
                    </p>
                  </div>
                  
                  {/* Manual Game Entry */}
                  <div className="space-y-3">
                    <div className="space-y-2">
                      <Label>Game Name *</Label>
                      <Input
                        data-testid="epic-game-name-input"
                        placeholder="Enter game name (e.g., Fortnite, Rocket League)"
                        value={epicGameName}
                        onChange={(e) => setEpicGameName(e.target.value)}
                        className="bg-black/50 border-white/10"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Box Art URL (optional)</Label>
                      <Input
                        data-testid="epic-boxart-input"
                        placeholder="https://..."
                        value={epicBoxArtUrl}
                        onChange={(e) => setEpicBoxArtUrl(e.target.value)}
                        className="bg-black/50 border-white/10"
                      />
                    </div>
                    
                    <Button
                      onClick={handleAddEpicGame}
                      disabled={epicAddLoading || !epicGameName.trim()}
                      className="w-full bg-white text-black hover:bg-gray-200 font-bold uppercase tracking-wider"
                      data-testid="epic-add-game-btn"
                    >
                      {epicAddLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Adding...
                        </>
                      ) : (
                        <>
                          <Plus className="w-4 h-4 mr-2" />
                          Add Epic Game
                        </>
                      )}
                    </Button>
                  </div>
                  
                  <div className="p-3 bg-black/30 rounded-lg text-sm text-white/50">
                    <p>
                      <span className="text-white/70">Tip:</span> Go to Settings to connect your Epic Account ID 
                      and try the experimental auto-sync feature.
                    </p>
                  </div>
                </TabsContent>
                
                {/* URL Import Tab */}
                <TabsContent value="url" className="space-y-4 mt-4">
                  {/* Supported Sites */}
                  <div className="p-3 bg-[#121212] rounded-lg border border-white/5">
                    <p className="text-white/60 text-sm mb-2">Supported websites:</p>
                    <div className="flex flex-wrap gap-2">
                      {SUPPORTED_SITES.map((site) => (
                        <span
                          key={site.name}
                          className="text-xs px-2 py-1 bg-white/5 rounded text-white/70"
                        >
                          {site.name} ({site.platform})
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* URL Input */}
                  <div className="space-y-2">
                    <Label>Game Page URL</Label>
                    <div className="flex gap-2">
                      <Input
                        data-testid="import-url-input"
                        placeholder="https://metagamerscore.com/game/ID-game-name"
                        value={importUrl}
                        onChange={(e) => setImportUrl(e.target.value)}
                        className="bg-black/50 border-white/10 flex-1"
                      />
                      <Button
                        onClick={handlePreviewUrl}
                        disabled={importLoading}
                        variant="outline"
                        className="border-white/20"
                      >
                        {importLoading ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          "Preview"
                        )}
                      </Button>
                    </div>
                  </div>

                  {/* Preview */}
                  {previewData && (
                    <div className="p-4 bg-[#121212] rounded-lg border border-white/10 space-y-3">
                      <h4 className="font-medium text-white">Preview:</h4>
                      <div className="flex gap-4">
                        {previewData.boxArtUrl && (
                          <img
                            src={previewData.boxArtUrl}
                            alt="Box Art"
                            className="w-20 h-28 object-contain rounded bg-black/50"
                          />
                        )}
                        <div className="flex-1 space-y-1">
                          <p className="text-white font-medium">{previewData.name || "Unknown"}</p>
                          <p className="text-white/50 text-sm">{previewData.system || previewData.platform}</p>
                          {previewData.publisher && (
                            <p className="text-white/40 text-xs">Publisher: {previewData.publisher}</p>
                          )}
                          {previewData.developer && (
                            <p className="text-white/40 text-xs">Developer: {previewData.developer}</p>
                          )}
                          {previewData.genre && (
                            <p className="text-white/40 text-xs">Genre: {previewData.genre}</p>
                          )}
                          {previewData.completionEstimate && (
                            <p className="text-white/40 text-xs">Est. Completion: {previewData.completionEstimate}</p>
                          )}
                          <p className="text-purple-400 text-sm">
                            {previewData.achievements?.length || 0} achievements found
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Import Button */}
                  <Button
                    onClick={handleImportGame}
                    disabled={importLoading || !importUrl.trim()}
                    data-testid="confirm-import-btn"
                    className="w-full bg-white text-black hover:bg-gray-200 font-bold uppercase tracking-wider"
                  >
                    {importLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Importing...
                      </>
                    ) : (
                      <>
                        <Globe className="w-4 h-4 mr-2" />
                        Import Game & Achievements
                      </>
                    )}
                  </Button>
                </TabsContent>
              </Tabs>
            </DialogContent>
          </Dialog>

          {/* Add Game - Master DB Search First */}
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetMasterDbState();
          }}>
            <DialogTrigger asChild>
              <Button
                data-testid="add-game-btn"
                className="bg-white text-black hover:bg-gray-200 font-bold uppercase tracking-wider"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Game
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-h-[90vh] overflow-y-auto max-w-2xl">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl">Add New Game</DialogTitle>
                <DialogDescription className="text-white/60">
                  Search the Master Database first, or add manually if not found.
                </DialogDescription>
              </DialogHeader>
              
              {/* Mode Toggle */}
              <div className="flex gap-2 border-b border-white/10 pb-4">
                <Button
                  variant={addGameMode === "masterdb" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setAddGameMode("masterdb")}
                  className={addGameMode === "masterdb" ? "bg-purple-600" : "border-white/20"}
                >
                  <BookOpen className="w-4 h-4 mr-2" />
                  Search Master DB
                </Button>
                <Button
                  variant={addGameMode === "manual" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setAddGameMode("manual")}
                  className={addGameMode === "manual" ? "bg-white text-black" : "border-white/20"}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Manually
                </Button>
                <Button
                  variant={addGameMode === "titleid" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setAddGameMode("titleid")}
                  className={addGameMode === "titleid" ? "bg-green-600" : "border-white/20"}
                  data-testid="import-by-title-id-tab"
                >
                  <Gamepad2 className="w-4 h-4 mr-2" />
                  Xbox Title ID
                </Button>
              </div>
              
              {addGameMode === "masterdb" ? (
                <div className="space-y-4 pt-2">
                  {/* Master DB Search */}
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                      <Input
                        placeholder="Search Master Database..."
                        value={masterDbSearch}
                        onChange={(e) => setMasterDbSearch(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && handleMasterDbSearch()}
                        className="pl-10 bg-black/50 border-white/10"
                      />
                    </div>
                    <Button 
                      onClick={handleMasterDbSearch}
                      disabled={masterDbLoading}
                      className="bg-purple-600 hover:bg-purple-500"
                    >
                      {masterDbLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Search"}
                    </Button>
                  </div>
                  
                  {/* Search Results - Click to Import */}
                  {masterDbResults.length > 0 && (
                    <div className="max-h-80 overflow-y-auto space-y-2 border border-white/10 rounded-lg p-2">
                      {masterDbResults.map((game) => (
                        <div
                          key={game.id}
                          className="p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
                        >
                          <div className="flex items-start gap-3">
                            <div className="w-14 h-20 rounded bg-white/10 overflow-hidden flex-shrink-0">
                              <img
                                src={game.boxArtUrl || "/placeholder-game.png"}
                                alt={game.name}
                                className="w-full h-full object-contain"
                                onError={(e) => e.target.src = "/placeholder-game.png"}
                              />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-white">{game.name}</p>
                              <p className="text-xs text-white/50 mt-0.5">
                                {game.totalAchievements || 0} achievements • {game.dlcs?.length || 0} DLCs
                                {game.publisher && ` • ${game.publisher}`}
                              </p>
                              {/* Platforms & Systems */}
                              <div className="flex flex-wrap gap-1 mt-2">
                                {game.platforms?.map(p => (
                                  <span key={p} className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-400 capitalize">
                                    {p}
                                  </span>
                                ))}
                                {game.systems?.map(s => (
                                  <span key={s} className="text-[10px] px-1.5 py-0.5 rounded bg-white/10 text-white/70">
                                    {getSystemName(s, systemsLookup)}
                                  </span>
                                ))}
                              </div>
                            </div>
                            <Button
                              size="sm"
                              onClick={() => handleImportFromMasterDb(game)}
                              disabled={masterDbImporting}
                              className="bg-green-600 hover:bg-green-500 flex-shrink-0"
                            >
                              {masterDbImporting ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <>
                                  <Download className="w-4 h-4 mr-1" />
                                  Import
                                </>
                              )}
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {masterDbResults.length === 0 && masterDbSearch && !masterDbLoading && (
                    <div className="text-center py-6 text-white/50 border border-white/10 rounded-lg">
                      <Database className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p>No games found in Master Database</p>
                      <Button
                        variant="link"
                        className="text-purple-400 mt-2"
                        onClick={() => setAddGameMode("manual")}
                      >
                        Add game manually instead
                      </Button>
                    </div>
                  )}
                </div>
              ) : addGameMode === "titleid" ? (
                /* Import by Xbox Title ID Mode */
                <div className="space-y-4 pt-2">
                  <p className="text-sm text-white/60">
                    Import an Xbox game directly by its Title ID. Useful for games that don't appear in your profile lookup.
                  </p>
                  <div className="space-y-2">
                    <Label htmlFor="title-id-input">Xbox Title ID *</Label>
                    <Input
                      id="title-id-input"
                      data-testid="title-id-input"
                      placeholder="e.g., 2212351283"
                      value={titleIdInput}
                      onChange={(e) => setTitleIdInput(e.target.value)}
                      className="bg-black/50 border-white/10 focus:border-white/40"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="title-id-game-name">Game Name (optional)</Label>
                    <Input
                      id="title-id-game-name"
                      data-testid="title-id-game-name-input"
                      placeholder="Will auto-detect if left blank"
                      value={titleIdGameName}
                      onChange={(e) => setTitleIdGameName(e.target.value)}
                      className="bg-black/50 border-white/10 focus:border-white/40"
                    />
                  </div>
                  <Button
                    onClick={handleImportByTitleId}
                    disabled={titleIdImporting || !titleIdInput.trim()}
                    className="w-full bg-green-600 hover:bg-green-500"
                    data-testid="import-by-title-id-btn"
                  >
                    {titleIdImporting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Importing & Syncing...
                      </>
                    ) : (
                      <>
                        <Gamepad2 className="w-4 h-4 mr-2" />
                        Import Game
                      </>
                    )}
                  </Button>
                </div>
              ) : (
                /* Manual Add Mode */
                <div className="space-y-4 pt-2">
                <div className="space-y-2">
                  <Label htmlFor="game-name">Game Name *</Label>
                  <Input
                    id="game-name"
                    data-testid="game-name-input"
                    placeholder="Enter game name"
                    value={newGame.name}
                    onChange={(e) => setNewGame({ ...newGame, name: e.target.value })}
                    className="bg-black/50 border-white/10 focus:border-white/40"
                  />
                </div>

                <PlatformSystemPicker
                  idPrefix="manual"
                  platforms={platforms}
                  systemsForPlatform={getPlatformSystems(newGame.platform)}
                  platform={newGame.platform}
                  system={newGame.system}
                  onPlatformChange={(value) => setNewGame({ ...newGame, platform: value, system: "" })}
                  onSystemChange={(value) => setNewGame({ ...newGame, system: value })}
                  onDataRefresh={fetchPlatformsAndSystems}
                />

                <div className="space-y-2">
                  <Label>Box Art (optional)</Label>
                  <div className="flex gap-2 mb-2">
                    <Button
                      type="button"
                      variant={imageMode === "url" ? "default" : "outline"}
                      size="sm"
                      onClick={() => setImageMode("url")}
                      className={imageMode === "url" ? "bg-white text-black" : "border-white/20"}
                    >
                      <LinkIcon className="w-4 h-4 mr-1" />
                      URL
                    </Button>
                    <Button
                      type="button"
                      variant={imageMode === "upload" ? "default" : "outline"}
                      size="sm"
                      onClick={() => setImageMode("upload")}
                      className={imageMode === "upload" ? "bg-white text-black" : "border-white/20"}
                    >
                      <Upload className="w-4 h-4 mr-1" />
                      Upload
                    </Button>
                  </div>
                  {imageMode === "url" ? (
                    <Input
                      data-testid="game-boxart-input"
                      placeholder="https://example.com/image.jpg"
                      value={newGame.boxArtUrl}
                      onChange={(e) => setNewGame({ ...newGame, boxArtUrl: e.target.value })}
                      className="bg-black/50 border-white/10 focus:border-white/40"
                    />
                  ) : (
                    <Input
                      type="file"
                      accept="image/*"
                      data-testid="game-boxart-upload"
                      onChange={(e) => e.target.files?.[0] && handleImageUpload(e.target.files[0])}
                      className="bg-black/50 border-white/10"
                    />
                  )}
                  {newGame.boxArtUrl && (
                    <div className="mt-2 flex items-center gap-2">
                      <img src={newGame.boxArtUrl} alt="Preview" className="w-16 h-20 rounded object-contain bg-black/50" />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setNewGame({ ...newGame, boxArtUrl: "" })}
                        className="text-red-400"
                      >
                        Remove
                      </Button>
                    </div>
                  )}
                </div>

                <Button
                  data-testid="save-game-btn"
                  onClick={handleCreateGame}
                  className="w-full bg-white text-black hover:bg-gray-200 font-bold uppercase tracking-wider"
                >
                  Add Game
                </Button>
                </div>
              )}
            </DialogContent>
          </Dialog>
          
          {/* Post-Import Sync Prompt Dialog */}
          <Dialog open={syncPromptOpen} onOpenChange={setSyncPromptOpen}>
            <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-md">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-yellow-400" />
                  Sync Unlock Status?
                </DialogTitle>
                <DialogDescription className="text-white/60">
                  Your {importedGameForSync?.platform === "xbox" ? "Xbox" : 
                        importedGameForSync?.platform === "playstation" ? "PlayStation" : 
                        importedGameForSync?.platform === "steam" ? "Steam" : "platform"} account is linked. 
                  Would you like to sync your achievement unlock status now?
                </DialogDescription>
              </DialogHeader>
              
              {importedGameForSync && (
                <div className="p-4 rounded-lg bg-white/5 border border-white/10">
                  <div className="flex gap-3">
                    <img
                      src={importedGameForSync.boxArtUrl || "/placeholder-game.png"}
                      alt={importedGameForSync.name}
                      className="w-16 h-20 rounded object-cover bg-white/10"
                      onError={(e) => e.target.src = "/placeholder-game.png"}
                    />
                    <div>
                      <p className="font-medium text-white">{importedGameForSync.name}</p>
                      <p className="text-sm text-white/50">{importedGameForSync.totalAchievements} achievements</p>
                      <p className="text-xs text-white/40 mt-1">
                        This will check which achievements you've already unlocked.
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="flex gap-3 mt-4">
                <Button
                  onClick={skipPostImportSync}
                  variant="outline"
                  className="flex-1 border-white/20"
                  disabled={postImportSyncing}
                >
                  Skip
                </Button>
                <Button
                  onClick={handlePostImportSync}
                  className="flex-1 bg-green-600 hover:bg-green-500"
                  disabled={postImportSyncing}
                >
                  {postImportSyncing ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Syncing...
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4 mr-2" />
                      Sync Now
                    </>
                  )}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
          <Input
            data-testid="search-games-input"
            placeholder="Search games..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-black/50 border-white/10 focus:border-white/40"
          />
        </div>
        
        {/* Combined Filters Button */}
        <Popover>
          <PopoverTrigger asChild>
            <Button 
              variant="outline" 
              className="w-full sm:w-auto bg-black/50 border-white/10 justify-start"
              data-testid="combined-filter-btn"
            >
              <Filter className="w-4 h-4 mr-2" />
              <span>Filters</span>
              {(platformFilter !== "all" || systemFilter !== "all" || regionFilter !== "all" || ownedFilter !== "all" || syncFilter !== "all" || contentTagFilter !== "all" || gameTagFilter !== "all" || genreFilters.length > 0 || revealHidden) && (
                <span className="ml-2 px-1.5 py-0.5 text-xs bg-purple-500/30 rounded">
                  {[platformFilter !== "all", systemFilter !== "all", regionFilter !== "all", ownedFilter !== "all", syncFilter !== "all", contentTagFilter !== "all", gameTagFilter !== "all", genreFilters.length > 0, revealHidden].filter(Boolean).length}
                </span>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80 bg-[#0A0A0A] border-white/10 p-0 max-h-[80vh] overflow-y-auto" align="start">
            <div className="p-3 border-b border-white/10">
              <h4 className="font-medium text-white text-sm">Filter Games</h4>
            </div>
            
            {/* Platform Section */}
            <div className="p-3 border-b border-white/10">
              <p className="text-xs text-white/50 mb-2 uppercase tracking-wide">Platform</p>
              <Select value={platformFilter} onValueChange={(val) => { setPlatformFilter(val); setSystemFilter("all"); }}>
                <SelectTrigger className="w-full bg-black/50 border-white/10" data-testid="filter-platform">
                  <SelectValue placeholder="All Platforms" />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-white/10">
                  <SelectItem value="all">All Platforms</SelectItem>
                  {sortedPlatforms.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      <span style={{ color: p.color }}>{p.name}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {/* System Sub-filter (only when platform selected & has >1 system) */}
              {platformFilter !== "all" && selectedPlatformSystems.length > 1 && (
                <div className="mt-2">
                  <p className="text-xs text-white/40 mb-1">System</p>
                  <Select value={systemFilter} onValueChange={setSystemFilter}>
                    <SelectTrigger className="w-full bg-black/50 border-white/10" data-testid="filter-system">
                      <SelectValue placeholder="All Systems" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-white/10">
                      <SelectItem value="all">All Systems</SelectItem>
                      {selectedPlatformSystems.map((sys) => (
                        <SelectItem key={sys.id || sys.name} value={sys.name}>
                          {sys.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            
            {/* Ownership Section (dropdown) */}
            <div className="p-3 border-b border-white/10">
              <p className="text-xs text-white/50 mb-2 uppercase tracking-wide">Ownership</p>
              <Select value={ownedFilter} onValueChange={setOwnedFilter}>
                <SelectTrigger className="w-full bg-black/50 border-white/10" data-testid="filter-ownership">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-white/10">
                  <SelectItem value="all">All Games</SelectItem>
                  <SelectItem value="owned">Owned</SelectItem>
                  <SelectItem value="not_owned">Not Owned</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Region Section (dropdown) */}
            {regions.length > 0 && (
              <div className="p-3 border-b border-white/10">
                <p className="text-xs text-white/50 mb-2 uppercase tracking-wide">Region</p>
                <Select value={regionFilter} onValueChange={setRegionFilter}>
                  <SelectTrigger className="w-full bg-black/50 border-white/10" data-testid="filter-region">
                    <SelectValue placeholder="All Regions" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A0A0A] border-white/10 max-h-64">
                    <SelectItem value="all">All Regions</SelectItem>
                    {regions.map((r) => (
                      <SelectItem key={r.id} value={r.code}>{r.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {/* Sync Status Section (dropdown) */}
            <div className="p-3 border-b border-white/10">
              <p className="text-xs text-white/50 mb-2 uppercase tracking-wide">Sync Status</p>
              <Select value={syncFilter} onValueChange={setSyncFilter}>
                <SelectTrigger className="w-full bg-black/50 border-white/10" data-testid="filter-sync">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-white/10">
                  <SelectItem value="all">All Sync Status</SelectItem>
                  <SelectItem value="incomplete">
                    <span className="flex items-center gap-1.5">
                      <AlertTriangle className="w-3 h-3 text-amber-500" />
                      Incomplete Sync
                    </span>
                  </SelectItem>
                  <SelectItem value="complete">Complete Sync</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Content Tags Section (MDB achievement tags) */}
            {masterTags.length > 0 && (
              <div className="p-3 border-b border-white/10">
                <p className="text-xs text-white/50 mb-2 uppercase tracking-wide">Content Tags</p>
                <Select value={contentTagFilter} onValueChange={setContentTagFilter}>
                  <SelectTrigger className="w-full bg-black/50 border-white/10" data-testid="filter-content-tag">
                    <SelectValue placeholder="All Content Tags" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A0A0A] border-white/10 max-h-64">
                    <SelectItem value="all">All Content Tags</SelectItem>
                    {[...masterTags].sort((a, b) => a.name.localeCompare(b.name)).map((tag) => (
                      <SelectItem key={tag.id} value={tag.id}>
                        <span className="flex items-center gap-2">
                          <span 
                            className="w-2.5 h-2.5 rounded-full" 
                            style={{ backgroundColor: tag.color || "#888" }}
                          />
                          {tag.name}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {/* Genres Section (collapsible dropdown with checkboxes) */}
            {genres.length > 0 && (
              <div className="p-3 border-b border-white/10">
                <button 
                  onClick={() => setGenresDropdownOpen(!genresDropdownOpen)}
                  className="w-full flex items-center justify-between text-xs text-white/50 uppercase tracking-wide hover:text-white/70 transition-colors"
                  data-testid="filter-genres-toggle"
                >
                  <span>Genres {genreFilters.length > 0 && `(${genreFilters.length})`}</span>
                  <svg 
                    className={`w-3.5 h-3.5 transition-transform ${genresDropdownOpen ? 'rotate-180' : ''}`} 
                    fill="none" viewBox="0 0 24 24" stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {genresDropdownOpen && (
                  <div className="mt-2 max-h-48 overflow-y-auto space-y-1">
                    {[...genres].sort((a, b) => a.name.localeCompare(b.name)).map(genre => (
                      <label 
                        key={genre.id}
                        className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer transition-colors ${
                          genreFilters.includes(genre.slug) ? 'bg-purple-500/20' : 'hover:bg-white/5'
                        }`}
                      >
                        <Checkbox
                          checked={genreFilters.includes(genre.slug)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setGenreFilters([...genreFilters, genre.slug]);
                            } else {
                              setGenreFilters(genreFilters.filter(g => g !== genre.slug));
                            }
                          }}
                          className="border-white/30"
                        />
                        <div 
                          className="w-2 h-2 rounded-full" 
                          style={{ backgroundColor: genre.color }}
                        />
                        <span className="text-sm text-white">{genre.name}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}
            
            {/* Reveal Hidden Checkbox */}
            <div className="p-3 border-b border-white/10">
              <label className="flex items-center gap-2 cursor-pointer group">
                <Checkbox
                  checked={revealHidden}
                  onCheckedChange={(checked) => setRevealHidden(!!checked)}
                  className="border-white/30"
                  data-testid="filter-reveal-hidden"
                />
                <span className="text-sm text-white group-hover:text-white/80 transition-colors">
                  Reveal Hidden
                </span>
                {showHiddenSetting && (
                  <span className="text-[10px] text-white/30 ml-auto">(on in settings)</span>
                )}
              </label>
            </div>
            
            {/* Personal Tags Section (user-created tags from settings) */}
            {gameTags.length > 0 && (
              <div className="p-3 border-b border-white/10">
                <p className="text-xs text-white/50 mb-2 uppercase tracking-wide">Personal Tags</p>
                <Select value={gameTagFilter} onValueChange={setGameTagFilter}>
                  <SelectTrigger className="w-full bg-black/50 border-white/10" data-testid="filter-personal-tag">
                    <SelectValue placeholder="All Tags" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A0A0A] border-white/10">
                    <SelectItem value="all">All Tags</SelectItem>
                    {gameTags.map((tag) => (
                      <SelectItem key={tag.id} value={tag.id}>
                        <span className="flex items-center gap-2">
                          <span 
                            className="w-3 h-3 rounded-full" 
                            style={{ backgroundColor: tag.color }}
                          />
                          {tag.name}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {/* Clear Filters */}
            {(platformFilter !== "all" || systemFilter !== "all" || regionFilter !== "all" || ownedFilter !== "all" || syncFilter !== "all" || contentTagFilter !== "all" || gameTagFilter !== "all" || genreFilters.length > 0 || revealHidden) && (
              <div className="p-3">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setPlatformFilter("all");
                    setSystemFilter("all");
                    setRegionFilter("all");
                    setOwnedFilter("all");
                    setSyncFilter("all");
                    setContentTagFilter("all");
                    setGameTagFilter("all");
                    setGenreFilters([]);
                    setRevealHidden(false);
                  }}
                  className="w-full text-xs text-white/50 hover:text-white"
                  data-testid="filter-clear-all"
                >
                  Clear all filters
                </Button>
              </div>
            )}
          </PopoverContent>
        </Popover>
        
        {/* Sort By */}
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger data-testid="sort-games-by" className="w-full sm:w-44 bg-black/50 border-white/10">
            <ArrowUpDown className="w-4 h-4 mr-2" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#0A0A0A] border-white/10">
            <SelectItem value="name">Name</SelectItem>
            <SelectItem value="completion">Completion %</SelectItem>
            <SelectItem value="score">Score</SelectItem>
            <SelectItem value="remainingTime">Time Remaining</SelectItem>
            <SelectItem value="createdAt">Date Added</SelectItem>
          </SelectContent>
        </Select>
        
        {/* Sort Order */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}
          className="text-white/50 hover:text-white px-3"
          data-testid="sort-games-order"
        >
          {sortOrder === "desc" ? "↓ Desc" : "↑ Asc"}
        </Button>
      </div>

      {/* Pagination Controls - Top */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-[#0A0A0A] p-3 rounded-lg border border-white/5">
        <div className="flex items-center gap-2">
          <span className="text-white/50 text-sm">Show:</span>
          <Select value={gamesPerPage.toString()} onValueChange={(v) => { setGamesPerPage(Number(v)); setCurrentPage(1); }}>
            <SelectTrigger className="w-20 bg-black/50 border-white/10 h-8">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#0A0A0A] border-white/10">
              <SelectItem value="20">20</SelectItem>
              <SelectItem value="50">50</SelectItem>
              <SelectItem value="100">100</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-white/50 text-sm">per page</span>
        </div>
        
        <div className="text-white/50 text-sm">
          Showing {startIndex + 1}-{Math.min(endIndex, totalGames)} of {totalGames} games
        </div>
        
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="sm"
            onClick={() => goToPage(1)}
            disabled={currentPage === 1}
            className="border-white/10 hover:bg-white/10 h-8 w-8 p-0"
          >
            <ChevronsLeft className="w-4 h-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => goToPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className="border-white/10 hover:bg-white/10 h-8 w-8 p-0"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="px-3 text-white text-sm">
            Page {currentPage} of {totalPages || 1}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => goToPage(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage >= totalPages}
            className="border-white/10 hover:bg-white/10 h-8 w-8 p-0"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => goToPage(totalPages)}
            disabled={currentPage >= totalPages}
            className="border-white/10 hover:bg-white/10 h-8 w-8 p-0"
          >
            <ChevronsRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Games Grid */}
      {loading ? (
        <GamesSkeleton />
      ) : displayedGames.length > 0 ? (
        <TooltipProvider delayDuration={300}>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {displayedGames.map((game, idx) => {
            const completionPct = game.totalAchievements > 0
              ? Math.round((game.unlockedAchievements / game.totalAchievements) * 100)
              : 0;
            const platformColor = getPlatformColor(game.platform);
            const isSelected = selectedGameIds.includes(game.id);
            
            return (
              <div key={game.id} className="relative group animate-fade-in-up" style={{ animationDelay: `${Math.min(idx, 14) * 0.03}s` }}>
                {/* Selection Mode Checkbox Overlay */}
                {selectionMode && (
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      toggleGameSelection(game.id);
                    }}
                    className={`absolute inset-0 z-10 flex items-center justify-center transition-colors ${
                      isSelected ? 'bg-green-500/20' : 'bg-black/40 hover:bg-black/60'
                    }`}
                    data-testid={`select-game-${game.id}`}
                  >
                    <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center transition-all ${
                      isSelected 
                        ? 'bg-green-500 border-green-500' 
                        : 'border-white/50 bg-black/50'
                    }`}>
                      {isSelected && <Check className="w-5 h-5 text-white" />}
                    </div>
                  </button>
                )}
                <Tooltip>
                  <TooltipTrigger asChild>
                <Link to={selectionMode ? '#' : `/games/${game.id}`} onClick={selectionMode ? (e) => e.preventDefault() : undefined}>
                  <Card
                    data-testid={`game-card-${game.id}`}
                    className={`relative overflow-hidden bg-[#0A0A0A] border-white/5 hover:border-white/20 transition-all hover:-translate-y-1 cursor-pointer ${
                      isSelected ? 'ring-2 ring-green-500' : ''
                    }`}
                  >
                    <CardContent className="p-0">
                      {/* Box Art with System Bar */}
                      <div className="relative bg-[#121212]">
                        <div className="flex flex-col">
                          {game.showSystemBar && game.boxArtUrl && (
                            <SystemBarOverlay 
                              platform={game.platforms?.[0]} 
                              system={game.systems?.[0]}
                              override={game.systemBarOverride}
                              show={true}
                            />
                          )}
                          <div className="aspect-[3/4] flex items-center justify-center bg-[#0A0A0A]">
                            {game.boxArtUrl ? (
                              <img
                                src={game.boxArtUrl}
                                alt={game.name}
                                className="max-w-full max-h-full object-contain"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Gamepad2 className="w-16 h-16 text-white/10" />
                              </div>
                            )}
                          </div>
                        </div>
                        {/* Status Badges - Top Right Corner */}
                        <div className="absolute top-2 right-2 flex gap-1">
                          {/* Priority Star Badge */}
                          {game.isPriority && (
                            <div 
                              className="p-1 rounded-full bg-black/60 backdrop-blur-sm"
                              title="Priority Game"
                              data-testid={`priority-star-${game.id}`}
                            >
                              <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                            </div>
                          )}
                          {/* Completed Badge */}
                          {(game.isManuallyComplete || game.completionPercentage >= 100) && (
                            <div 
                              className="p-1 rounded-full bg-black/60 backdrop-blur-sm"
                              title="Completed"
                              data-testid={`completed-badge-${game.id}`}
                            >
                              <CheckCircle2 className="w-4 h-4 text-green-400 fill-green-400" />
                            </div>
                          )}
                          {/* Backlog Badge */}
                          {game.isBacklog && (
                            <div 
                              className="p-1 rounded-full bg-black/60 backdrop-blur-sm"
                              title="Backlog"
                              data-testid={`backlog-badge-${game.id}`}
                            >
                              <Archive className="w-4 h-4 text-slate-400" />
                            </div>
                          )}
                          {/* Hidden Badge */}
                          {game.isHidden && (
                            <div 
                              className="p-1 rounded-full bg-black/60 backdrop-blur-sm"
                              title="Hidden Game"
                              data-testid={`hidden-badge-${game.id}`}
                            >
                              <EyeOff className="w-4 h-4 text-gray-400" />
                            </div>
                          )}
                          {/* Incomplete Sync Badge */}
                          {hasIncompleteSync(game) && (
                            <div 
                              className="p-1 rounded-full bg-black/60 backdrop-blur-sm cursor-pointer"
                              title="Incomplete sync - click for details"
                              data-testid={`incomplete-sync-badge-${game.id}`}
                              onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleIncompleteSyncClick(e, game); }}
                            >
                              <AlertTriangle className="w-4 h-4 text-amber-500" />
                            </div>
                          )}
                          {/* Copy Number Badge */}
                          {game.copyNumber && (
                            <div 
                              className="px-1.5 py-0.5 rounded-full bg-black/60 backdrop-blur-sm flex items-center gap-0.5"
                              title={`Copy #${game.copyNumber}`}
                              data-testid={`copy-badge-${game.id}`}
                            >
                              <Copy className="w-3 h-3 text-blue-400" />
                              <span className="text-blue-400 text-xs font-medium">{game.copyNumber}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Info */}
                      <div className="p-3">
                        <div className="flex items-start justify-between gap-1">
                          <h3 className="text-white font-medium text-sm truncate flex-1">{game.name}</h3>
                          {/* Backlog Toggle Button (show for games with 0% progress) */}
                          {game.unlockedAchievements === 0 && !game.isBacklog && (
                            <button
                              onClick={(e) => handleToggleBacklog(e, game)}
                              className="flex-shrink-0 p-1 rounded hover:bg-slate-500/20 transition-colors opacity-50 hover:opacity-100"
                              title="Add to Backlog"
                              data-testid={`add-backlog-${game.id}`}
                            >
                              <Archive className="w-4 h-4 text-slate-400" />
                            </button>
                          )}
                          {/* Remove from Backlog Button */}
                          {game.isBacklog && (
                            <button
                              onClick={(e) => handleToggleBacklog(e, game)}
                              className="flex-shrink-0 p-1 rounded hover:bg-slate-500/20 transition-colors"
                              title="Remove from Backlog"
                              data-testid={`remove-backlog-${game.id}`}
                            >
                              <Archive className="w-4 h-4 text-slate-400 fill-slate-400" />
                            </button>
                          )}
                        </div>
                        {/* Platform and System Tags */}
                        <div className="flex items-center gap-1 mt-1 flex-wrap">
                          <PlatformBadge platform={game.platform} size="sm" />
                          <SystemBadge system={game.system} platform={game.platform} size="sm" />
                          {game.isAchievementSlop && <SlopBadge compact />}
                          {(() => {
                            // Release year — extracted as the first 4-digit year token from releaseDate.
                            // Helps users distinguish between same-name remasters/ports across years.
                            const m = game.releaseDate ? String(game.releaseDate).match(/(\d{4})/) : null;
                            if (!m) return null;
                            return (
                              <span
                                className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-white/10 text-white/70 border border-white/10"
                                data-testid={`release-year-${game.id}`}
                                title={`Released ${m[1]}`}
                              >
                                {m[1]}
                              </span>
                            );
                          })()}
                          {(() => {
                            const st = dbStatusMap[game.id];
                            if (!st) return null;
                            const cfg = {
                              official: { Icon: ShieldCheck, cls: "text-green-400", title: "Officially recognized — linked to the Master Database" },
                              unlinked: { Icon: Unlink, cls: "text-amber-400", title: "Unlinked — a matching game exists in the Master Database but this copy isn't linked" },
                              custom: { Icon: User, cls: "text-white/40", title: "Custom game — entered by you, no Master Database match" },
                            }[st];
                            if (!cfg) return null;
                            const { Icon, cls, title } = cfg;
                            return (
                              <span
                                className={`inline-flex items-center ${cls}`}
                                title={title}
                                aria-label={title}
                                data-testid={`db-status-${st}-${game.id}`}
                              >
                                <Icon className="w-3.5 h-3.5" />
                              </span>
                            );
                          })()}
                        </div>
                        
                        {/* Achievement Progress */}
                        <div className="mt-2 space-y-1">
                          <div className="flex justify-between text-xs">
                            <span className="text-white/50">
                              <Trophy className="w-3 h-3 inline mr-1" />
                              {game.unlockedAchievements}/{game.totalAchievements}
                            </span>
                            <span className="text-white/50">{completionPct}%</span>
                          </div>
                          <Progress value={completionPct} className={`h-1 ${getCompletionColor(completionPct)}`} />
                        </div>
                        
                        {/* Score Progress */}
                        {game.totalScore > 0 && (
                          <div className="mt-1.5 space-y-1">
                            <div className="flex justify-between text-xs">
                              <span className="text-yellow-400/70">
                                {game.earnedScore}/{game.totalScore} {getScoreAbbr(game.platform)}
                              </span>
                              <span className="text-yellow-400/70">
                                {Math.round((game.earnedScore / game.totalScore) * 100)}%
                              </span>
                            </div>
                            <Progress 
                              value={(game.earnedScore / game.totalScore) * 100} 
                              className="h-1 [&>div]:bg-yellow-500"
                            />
                          </div>
                        )}
                        
                        {/* Completion Time Estimate */}
                        {game.remainingHoursAvg !== null && game.remainingHoursAvg > 0 && (
                          <div className="mt-1.5 flex items-center gap-1.5 text-xs text-cyan-400/70">
                            <Clock className="w-3 h-3" />
                            <span>
                              {game.remainingHoursMin === game.remainingHoursMax
                                ? `${game.remainingHoursAvg}h left`
                                : `${game.remainingHoursMin}-${game.remainingHoursMax}h left`}
                            </span>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </Link>
                  </TooltipTrigger>
                  <TooltipContent
                    side="top"
                    sideOffset={8}
                    className="max-w-[320px] bg-[#101010] border border-white/10 text-white px-3 py-2.5 shadow-xl"
                    data-testid={`game-card-tooltip-${game.id}`}
                  >
                    {(() => {
                      const platName = (platforms.find((p) => p.id === game.platform)?.name) || game.platform || "Unknown";
                      // System row: show all playable systems with the primary first,
                      // resolving slugs (e.g. "xbox-one") to display names ("Xbox One")
                      // and de-duplicating by canonical alphanumeric key so a slug and
                      // its display-name twin never appear twice.
                      const primarySys = game.system || (Array.isArray(game.systems) ? game.systems[0] : "");
                      const otherSystems = Array.isArray(game.systems) ? game.systems.filter(Boolean) : [];
                      const seenSys = new Set();
                      const orderedSys = [];
                      [primarySys, ...otherSystems].forEach((raw) => {
                        if (!raw) return;
                        const display = getSystemName(raw, systemsLookup) || String(raw);
                        const key = display.replace(/[^a-z0-9]/gi, "").toLowerCase();
                        if (!key || seenSys.has(key)) return;
                        seenSys.add(key);
                        orderedSys.push(display);
                      });
                      const sysName = orderedSys.length ? orderedSys.join(", ") : "—";
                      const formatFullDate = (d) => {
                        if (!d) return "Unknown";
                        const s = String(d);
                        // Match YYYY, YYYY-MM, or YYYY-MM-DD
                        const m = s.match(/^(\d{4})(?:-(\d{2}))?(?:-(\d{2}))?/);
                        if (!m) return s;
                        const [, y, mo, da] = m;
                        if (!mo) return y;
                        const dt = new Date(`${y}-${mo}-${da || "01"}T00:00:00Z`);
                        if (Number.isNaN(dt.getTime())) return s;
                        return da
                          ? dt.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric", timeZone: "UTC" })
                          : dt.toLocaleDateString(undefined, { year: "numeric", month: "long", timeZone: "UTC" });
                      };
                      const genresList = Array.isArray(game.genres)
                        ? game.genres.filter(Boolean).map((g) => getGenreDisplayName(g))
                        : [];
                      const rows = [
                        ["Released", formatFullDate(game.releaseDate)],
                        ["Publisher", game.publisher || "Unknown"],
                        ["Developer", game.developer || "Unknown"],
                        ["Genres", genresList.length ? genresList.join(", ") : "—"],
                        ["Platform", platName],
                        ["System", sysName],
                      ];
                      return (
                        <div className="space-y-1.5">
                          <div className="text-sm font-semibold text-white mb-1 leading-snug">
                            {game.name}
                          </div>
                          {rows.map(([label, value]) => (
                            <div key={label} className="flex gap-2 text-[11px] leading-snug">
                              <span className="text-white/40 shrink-0 w-[64px]">{label}</span>
                              <span className="text-white/85 break-words">{value}</span>
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </TooltipContent>
                </Tooltip>
                
              </div>
            );
          })}
        </div>
        </TooltipProvider>
      ) : (
        <div className="text-center py-16">
          <Gamepad2 className="w-16 h-16 mx-auto mb-4 text-white/10" />
          <h3 className="text-white/50 text-lg">No games found</h3>
          <p className="text-white/30 text-sm mt-1">
            {searchQuery || platformFilter !== "all"
              ? "Try adjusting your filters"
              : "Add your first game to get started!"}
          </p>
        </div>
      )}

      {/* Pagination Controls - Bottom */}
      {totalGames > 0 && (
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 bg-[#0A0A0A] p-3 rounded-lg border border-white/5">
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              onClick={() => goToPage(1)}
              disabled={currentPage === 1}
              className="border-white/10 hover:bg-white/10 h-8 w-8 p-0"
            >
              <ChevronsLeft className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => goToPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="border-white/10 hover:bg-white/10 h-8 px-3"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Previous
            </Button>
            <span className="px-4 text-white text-sm">
              Page {currentPage} of {totalPages || 1}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => goToPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage >= totalPages}
              className="border-white/10 hover:bg-white/10 h-8 px-3"
            >
              Next
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => goToPage(totalPages)}
              disabled={currentPage >= totalPages}
              className="border-white/10 hover:bg-white/10 h-8 w-8 p-0"
            >
              <ChevronsRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Incomplete Sync Dialog */}
      <Dialog open={incompleteSyncDialogOpen} onOpenChange={setIncompleteSyncDialogOpen}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Incomplete Achievement Data
            </DialogTitle>
            <DialogDescription className="text-white/60">
              Some achievement data could not be synced automatically
            </DialogDescription>
          </DialogHeader>
          
          {selectedIncompleteGame && (
            <div className="space-y-4 pt-2">
              {/* Game Info */}
              <div className="flex items-center gap-3 p-3 bg-[#121212] rounded-lg border border-white/5">
                {selectedIncompleteGame.boxArtUrl ? (
                  <img
                    src={selectedIncompleteGame.boxArtUrl}
                    alt={selectedIncompleteGame.name}
                    className="w-12 h-16 object-contain rounded bg-black/50"
                  />
                ) : (
                  <div className="w-12 h-16 bg-white/5 rounded flex items-center justify-center">
                    <Gamepad2 className="w-6 h-6 text-white/20" />
                  </div>
                )}
                <div>
                  <p className="text-white font-medium">{selectedIncompleteGame.name}</p>
                  <p className="text-white/50 text-sm">{selectedIncompleteGame.system}</p>
                </div>
              </div>

              {/* Stats Comparison */}
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-[#121212] rounded-lg border border-white/5 text-center">
                  <p className="text-white/50 text-xs uppercase mb-1">Synced</p>
                  <p className="text-white text-xl font-bold">{selectedIncompleteGame.totalAchievements}</p>
                  <p className="text-white/40 text-xs">achievements</p>
                </div>
                <div className="p-3 bg-amber-500/10 rounded-lg border border-amber-500/20 text-center">
                  <p className="text-amber-400 text-xs uppercase mb-1">Xbox Reports</p>
                  <p className="text-amber-400 text-xl font-bold">{selectedIncompleteGame.xboxTotalAchievements || '?'}</p>
                  <p className="text-amber-400/60 text-xs">achievements</p>
                </div>
              </div>

              {/* Explanation */}
              <div className="p-3 bg-[#121212] rounded-lg border border-white/5 space-y-2">
                <div className="flex items-start gap-2">
                  <Info className="w-4 h-4 text-white/50 mt-0.5 flex-shrink-0" />
                  <div className="text-white/70 text-sm">
                    <p className="mb-2">
                      <strong>Why is this happening?</strong>
                    </p>
                    <p className="text-white/50">
                      The Xbox API doesn&apos;t provide locked (unearned) achievement details for older titles like Xbox 360 games. 
                      Only achievements you&apos;ve already unlocked can be synced automatically.
                    </p>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="space-y-3">
                {/* Auto-Sync Attempt */}
                <div className="space-y-2">
                  <p className="text-white/60 text-sm font-medium">Option 1: Try Auto-Sync</p>
                  <Button
                    onClick={() => handleAutoSyncAttempt(selectedIncompleteGame)}
                    disabled={autoSyncLoading}
                    className="w-full bg-[#107C10] hover:bg-[#0e6b0e] text-white"
                    data-testid="auto-sync-btn"
                  >
                    {autoSyncLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Attempting to fetch...
                      </>
                    ) : (
                      <>
                        <Download className="w-4 h-4 mr-2" />
                        Fetch Missing Achievements from Xbox
                      </>
                    )}
                  </Button>
                  <p className="text-white/40 text-xs text-center">
                    May not work for Xbox 360 titles due to API limitations
                  </p>
                </div>

                {/* Divider */}
                <div className="flex items-center gap-3">
                  <div className="flex-1 border-t border-white/10"></div>
                  <span className="text-white/30 text-xs">OR</span>
                  <div className="flex-1 border-t border-white/10"></div>
                </div>

                {/* Manual Entry */}
                <div className="space-y-2">
                  <p className="text-white/60 text-sm font-medium">Option 2: Manual Entry</p>
                  <Button
                    onClick={() => {
                      setManualEntryGame(selectedIncompleteGame);
                      setIncompleteSyncDialogOpen(false);
                      setManualEntryDialogOpen(true);
                    }}
                    variant="outline"
                    className="w-full border-white/20 text-white hover:bg-white/10"
                    data-testid="manual-entry-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Bulk Add Achievements Manually
                  </Button>
                </div>

                {/* TrueAchievements Link */}
                <a
                  href={getTrueAchievementsUrl(selectedIncompleteGame.name)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 w-full p-2 text-white/60 hover:text-white text-sm transition-colors"
                  data-testid="trueachievements-link"
                >
                  <ExternalLink className="w-3 h-3" />
                  View on TrueAchievements for reference
                </a>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Manual Achievement Entry Dialog */}
      <Dialog open={manualEntryDialogOpen} onOpenChange={setManualEntryDialogOpen}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <Plus className="w-5 h-5 text-[#107C10]" />
              Bulk Add Achievements
            </DialogTitle>
            <DialogDescription className="text-white/60">
              {manualEntryGame?.name} - Add missing achievements
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 pt-2">
            {/* Instructions */}
            <div className="p-3 bg-[#121212] rounded-lg border border-white/5">
              <p className="text-white/70 text-sm mb-2">
                <strong>How to use:</strong>
              </p>
              <ol className="text-white/50 text-xs space-y-1 list-decimal list-inside">
                <li>Copy achievement data from TrueAchievements or another source</li>
                <li>Paste below in the format: <code className="bg-white/10 px-1 rounded">Name | Description | Score</code></li>
                <li>One achievement per line</li>
              </ol>
            </div>

            {/* Quick Open TrueAchievements */}
            {manualEntryGame && (
              <a
                href={getTrueAchievementsUrl(manualEntryGame.name)}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full p-2 bg-[#107C10]/20 hover:bg-[#107C10]/30 text-[#107C10] rounded-lg text-sm transition-colors"
              >
                <ExternalLink className="w-4 h-4" />
                Open TrueAchievements in new tab
              </a>
            )}

            {/* Text Input */}
            <div className="space-y-2">
              <Label>Achievement Data (one per line)</Label>
              <textarea
                value={bulkAchievementText}
                onChange={(e) => setBulkAchievementText(e.target.value)}
                placeholder={`Achievement Name | Description | 10\nAnother Achievement | Some description | 25\nSecret Achievement | Hidden until unlocked | 50`}
                className="w-full h-48 p-3 bg-black/50 border border-white/10 rounded-lg text-white text-sm font-mono resize-none focus:border-white/40 focus:outline-none"
                data-testid="bulk-achievement-input"
              />
              <p className="text-white/40 text-xs">
                Format: Name | Description | Gamerscore (description and score are optional)
              </p>
            </div>

            {/* Preview */}
            {bulkAchievementText.trim() && (
              <div className="space-y-2">
                <Label className="text-white/60">Preview ({parseBulkAchievements(bulkAchievementText).length} achievements)</Label>
                <div className="max-h-32 overflow-y-auto bg-[#121212] rounded-lg border border-white/5 p-2">
                  {parseBulkAchievements(bulkAchievementText).slice(0, 5).map((ach, idx) => (
                    <div key={idx} className="flex justify-between items-center py-1 text-xs border-b border-white/5 last:border-0">
                      <span className="text-white truncate flex-1">{ach.name}</span>
                      <span className="text-yellow-400 ml-2">{ach.score}G</span>
                    </div>
                  ))}
                  {parseBulkAchievements(bulkAchievementText).length > 5 && (
                    <p className="text-white/40 text-xs pt-1">
                      ...and {parseBulkAchievements(bulkAchievementText).length - 5} more
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Submit */}
            <Button
              onClick={handleBulkAddAchievements}
              disabled={bulkAddLoading || !bulkAchievementText.trim()}
              className="w-full bg-white text-black hover:bg-gray-200 font-bold uppercase tracking-wider"
              data-testid="bulk-add-submit-btn"
            >
              {bulkAddLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Adding Achievements...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  Add {parseBulkAchievements(bulkAchievementText).length || 0} Achievements
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Bulk Edit Dialog */}
      <Dialog open={bulkEditDialogOpen} onOpenChange={(open) => {
        if (!open) {
          exitSelectionMode();
        }
        setBulkEditDialogOpen(open);
      }}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-lg max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>Edit Game ({currentEditIndex + 1} of {editQueue.length})</span>
            </DialogTitle>
            <DialogDescription className="text-white/60">
              Edit game details. Save to apply changes, or Skip to move to the next game.
            </DialogDescription>
          </DialogHeader>
          
          {bulkEditGameData && (
            <div className="space-y-4 overflow-y-auto flex-1 pr-2">
              {/* Game Name */}
              <div className="space-y-2">
                <Label>Game Name</Label>
                <Input
                  value={bulkEditGameData.name || ""}
                  onChange={(e) => setBulkEditGameData({...bulkEditGameData, name: e.target.value})}
                  className="bg-black/50 border-white/10"
                />
              </div>
              
              {/* Platform & System */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Platform</Label>
                  <Select 
                    value={bulkEditGameData.platform || ""} 
                    onValueChange={(v) => setBulkEditGameData({...bulkEditGameData, platform: v})}
                  >
                    <SelectTrigger className="bg-black/50 border-white/10">
                      <SelectValue placeholder="Select platform" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-white/10">
                      {platforms.map(p => (
                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>System</Label>
                  <Input
                    value={bulkEditGameData.system || ""}
                    onChange={(e) => setBulkEditGameData({...bulkEditGameData, system: e.target.value})}
                    className="bg-black/50 border-white/10"
                    placeholder="e.g., Xbox Series X"
                  />
                </div>
              </div>
              
              {/* Publisher & Developer */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Publisher</Label>
                  <Input
                    value={bulkEditGameData.publisher || ""}
                    onChange={(e) => setBulkEditGameData({...bulkEditGameData, publisher: e.target.value})}
                    className="bg-black/50 border-white/10"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Developer</Label>
                  <Input
                    value={bulkEditGameData.developer || ""}
                    onChange={(e) => setBulkEditGameData({...bulkEditGameData, developer: e.target.value})}
                    className="bg-black/50 border-white/10"
                  />
                </div>
              </div>
              
              {/* Release Date */}
              <div className="space-y-2">
                <Label>Release Date</Label>
                <Input
                  value={bulkEditGameData.releaseDate || ""}
                  onChange={(e) => setBulkEditGameData({...bulkEditGameData, releaseDate: e.target.value})}
                  className="bg-black/50 border-white/10"
                  placeholder="YYYY-MM-DD"
                />
              </div>
              
              {/* Genres */}
              <div className="space-y-2">
                <Label>Genres</Label>
                <div className="flex flex-wrap gap-2 p-2 border border-white/10 rounded-lg bg-black/50 max-h-24 overflow-y-auto">
                  {[...genres].sort((a, b) => a.name.localeCompare(b.name)).map((genre) => (
                    <label 
                      key={genre.id} 
                      className={`flex items-center gap-1.5 px-2 py-1 rounded cursor-pointer text-xs transition-colors ${
                        (bulkEditGameData.genres || []).includes(genre.slug) 
                          ? 'bg-purple-500/30 border border-purple-500/50' 
                          : 'bg-white/5 hover:bg-white/10'
                      }`}
                    >
                      <Checkbox
                        checked={(bulkEditGameData.genres || []).includes(genre.slug)}
                        onCheckedChange={(checked) => {
                          const currentGenres = bulkEditGameData.genres || [];
                          if (checked) {
                            setBulkEditGameData({...bulkEditGameData, genres: [...currentGenres, genre.slug]});
                          } else {
                            setBulkEditGameData({...bulkEditGameData, genres: currentGenres.filter(g => g !== genre.slug)});
                          }
                        }}
                        className="border-white/30 w-3 h-3"
                      />
                      <div className="w-2 h-2 rounded-full" style={{ backgroundColor: genre.color }} />
                      <span className="text-white">{genre.name}</span>
                    </label>
                  ))}
                </div>
              </div>
              
              {/* Game Tags (Favorite, NEEDS ATTENTION, etc.) */}
              {gameTags.length > 0 && (
                <div className="space-y-2">
                  <Label>Game Tags</Label>
                  <div className="flex flex-wrap gap-2 p-2 border border-white/10 rounded-lg bg-black/50">
                    {gameTags.map((tag) => (
                      <label 
                        key={tag.id} 
                        className={`flex items-center gap-1.5 px-2 py-1 rounded cursor-pointer text-xs transition-colors ${
                          (bulkEditGameData.gameTags || []).includes(tag.id) 
                            ? 'bg-amber-500/30 border border-amber-500/50' 
                            : 'bg-white/5 hover:bg-white/10'
                        }`}
                      >
                        <Checkbox
                          checked={(bulkEditGameData.gameTags || []).includes(tag.id)}
                          onCheckedChange={(checked) => {
                            const currentTags = bulkEditGameData.gameTags || [];
                            if (checked) {
                              setBulkEditGameData({...bulkEditGameData, gameTags: [...currentTags, tag.id]});
                            } else {
                              setBulkEditGameData({...bulkEditGameData, gameTags: currentTags.filter(t => t !== tag.id)});
                            }
                          }}
                          className="border-amber-500/50 w-3 h-3"
                        />
                        <Tag className="w-3 h-3" style={{ color: tag.color || '#F59E0B' }} />
                        <span className="text-white">{tag.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Quick Actions: Priority, Completed, Backlog, Hidden */}
              <div className="space-y-2">
                <Label>Quick Actions</Label>
                <div className="flex flex-wrap gap-3">
                  <label className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors ${
                    bulkEditGameData.isPriority ? 'bg-yellow-500/20 border border-yellow-500/50' : 'bg-white/5 hover:bg-white/10'
                  }`}>
                    <Checkbox
                      checked={bulkEditGameData.isPriority || false}
                      onCheckedChange={(checked) => setBulkEditGameData({...bulkEditGameData, isPriority: checked})}
                      className="border-yellow-500/50"
                    />
                    <Star className={`w-4 h-4 ${bulkEditGameData.isPriority ? 'text-yellow-400 fill-yellow-400' : 'text-white/50'}`} />
                    <span className="text-sm text-white">Priority</span>
                  </label>
                  
                  <label className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors ${
                    bulkEditGameData.isManualComplete ? 'bg-green-500/20 border border-green-500/50' : 'bg-white/5 hover:bg-white/10'
                  }`}>
                    <Checkbox
                      checked={bulkEditGameData.isManualComplete || false}
                      onCheckedChange={(checked) => setBulkEditGameData({...bulkEditGameData, isManualComplete: checked})}
                      className="border-green-500/50"
                    />
                    <Trophy className={`w-4 h-4 ${bulkEditGameData.isManualComplete ? 'text-green-400' : 'text-white/50'}`} />
                    <span className="text-sm text-white">Completed</span>
                  </label>
                  
                  <label className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors ${
                    bulkEditGameData.isBacklog ? 'bg-slate-500/20 border border-slate-500/50' : 'bg-white/5 hover:bg-white/10'
                  }`}>
                    <Checkbox
                      checked={bulkEditGameData.isBacklog || false}
                      onCheckedChange={(checked) => setBulkEditGameData({...bulkEditGameData, isBacklog: checked})}
                      className="border-slate-500/50"
                    />
                    <Archive className={`w-4 h-4 ${bulkEditGameData.isBacklog ? 'text-slate-400' : 'text-white/50'}`} />
                    <span className="text-sm text-white">Backlog</span>
                  </label>
                  
                  <label className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors ${
                    bulkEditGameData.isHidden ? 'bg-gray-500/20 border border-gray-500/50' : 'bg-white/5 hover:bg-white/10'
                  }`}>
                    <Checkbox
                      checked={bulkEditGameData.isHidden || false}
                      onCheckedChange={(checked) => setBulkEditGameData({...bulkEditGameData, isHidden: checked})}
                      className="border-gray-500/50"
                    />
                    <EyeOff className={`w-4 h-4 ${bulkEditGameData.isHidden ? 'text-gray-400' : 'text-white/50'}`} />
                    <span className="text-sm text-white">Hidden</span>
                  </label>
                </div>
              </div>
            </div>
          )}
          
          {/* Action Buttons */}
          <div className="flex gap-3 pt-4 border-t border-white/10 mt-4">
            <Button
              variant="outline"
              onClick={handleBulkEditSkip}
              disabled={bulkEditSaving}
              className="flex-1 border-white/20"
            >
              Skip
            </Button>
            <Button
              onClick={handleBulkEditSave}
              disabled={bulkEditSaving}
              className="flex-1 bg-green-600 hover:bg-green-500"
            >
              {bulkEditSaving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Bulk Delete Confirmation Dialog */}
      <Dialog open={bulkDeleteDialogOpen} onOpenChange={setBulkDeleteDialogOpen}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-400">
              <AlertTriangle className="w-5 h-5" />
              Delete {selectedGameIds.length} Game{selectedGameIds.length !== 1 ? 's' : ''}?
            </DialogTitle>
            <DialogDescription className="text-white/60">
              This action cannot be undone. All selected games and their achievements will be permanently deleted from your library.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 pt-4">
            {/* List of games to be deleted */}
            <div className="max-h-40 overflow-y-auto space-y-1 p-2 bg-red-500/5 border border-red-500/20 rounded-lg">
              {selectedGameIds.map(id => {
                const game = games.find(g => g.id === id);
                return game ? (
                  <div key={id} className="text-sm text-white/70 flex items-center gap-2">
                    <Trash2 className="w-3 h-3 text-red-400 flex-shrink-0" />
                    <span className="truncate">{game.name}</span>
                  </div>
                ) : null;
              })}
            </div>
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setBulkDeleteDialogOpen(false)}
                disabled={bulkDeleting}
                className="flex-1 border-white/20"
              >
                Cancel
              </Button>
              <Button
                onClick={handleBulkDelete}
                disabled={bulkDeleting}
                className="flex-1 bg-red-600 hover:bg-red-500"
              >
                {bulkDeleting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete {selectedGameIds.length} Game{selectedGameIds.length !== 1 ? 's' : ''}
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* AdSense */}
      <AdBanner />
    </div>
  );
}
